<template>
    <div :class="{fullscreen: isFullscreen, 'workflow-builder': true}" :style="isFullscreen ? {} : {position: 'relative', zIndex: 8}">
        <!-- Right Side Node Sidebar -->
        <div class="workflow-sidebar-right" :class="{ open: sidebarOpen && !readonly }" v-if="!readonly">
            <div class="sidebar-header">
                <div class="sidebar-header-left">
                    <b-icon icon="plus-square" class="sidebar-header-icon"/>
                    <span class="sidebar-title">{{ $t('addNode') }}</span>
                </div>
                <b-button 
                    variant="link"
                    size="sm"
                    @click="closeSidebar"
                    class="close-btn"
                >
                    <b-icon icon="x-lg"/>
                </b-button>
            </div>
            
            <!-- Search -->
            <div class="sidebar-search">
                <div class="search-input-wrap">
                    <b-icon icon="search" class="search-icon-left"/>
                    <b-form-input
                        v-model="nodeSearchFilter"
                        :placeholder="$t('searchNodes')"
                        size="sm"
                        class="node-search-input"
                    />
                    <b-button
                        v-if="nodeSearchFilter"
                        variant="link"
                        size="sm"
                        class="search-clear-btn"
                        @click="nodeSearchFilter = ''"
                    >
                        <b-icon icon="x"/>
                    </b-button>
                </div>
            </div>

            <!-- Category Tabs -->
            <div class="sidebar-tabs">
                <button
                    class="sidebar-tab"
                    :class="{ active: activeCategoryFilter === 'all' }"
                    @click="setCategoryFilter('all')"
                >
                    {{ $t('all') }}
                </button>
                <button
                    class="sidebar-tab"
                    :class="{ active: activeCategoryFilter === 'events' }"
                    @click="setCategoryFilter('events')"
                >
                    <b-icon icon="lightning-charge"/>
                    {{ $t('events') }}
                </button>
                <button
                    class="sidebar-tab"
                    :class="{ active: activeCategoryFilter === 'operations' }"
                    @click="setCategoryFilter('operations')"
                >
                    <b-icon icon="gear"/>
                    {{ $t('operations') }}
                </button>
                <button
                    class="sidebar-tab"
                    :class="{ active: activeCategoryFilter === 'logic' }"
                    @click="setCategoryFilter('logic')"
                >
                    <b-icon icon="diagram-3"/>
                    {{ $t('logic') }}
                </button>
            </div>
            
            <div class="sidebar-content">
                <div class="node-categories">
                    <!-- Triggers Section -->
                    <div class="node-category" v-if="shouldShowCategory('events') && filteredTriggerNodes.length > 0">
                        <div class="category-label">
                            <b-icon icon="lightning-charge" class="category-icon events-icon"/>
                            <span>{{ $t('events') }}</span>
                            <span class="node-count">{{ filteredTriggerNodes.length }}</span>
                        </div>
                        <div class="category-nodes">
                            <div 
                                v-for="nodeType in filteredTriggerNodes" 
                                :key="`trigger-${nodeType.type}-${nodeType.action || nodeType.eventName}-${nodeType._integration && nodeType._integration.integration_id ? nodeType._integration.integration_id : 'platform'}`"
                                class="draggable-node"
                                :class="[`${nodeType.type}-node`, `${nodeType.action}-node`, { 'integration-operation-node': isIntegrationOperationNode(nodeType) }]"
                                :data-type="nodeType.type"
                                :data-action="nodeType.action"
                                draggable="true"
                                @dragstart="onDragStart($event, nodeType)"
                                @dragend="onDragEnd"
                                :title="buildNodeTooltip(nodeType)"
                            >
                                <div class="node-icon-wrapper">
                                    <b-icon v-if="parseIcon(nodeType.icon).type === 'b-icon'" :icon="parseIcon(nodeType.icon).value" class="node-icon" />
                                    <img v-else-if="parseIcon(nodeType.icon).type === 'image'" :src="parseIcon(nodeType.icon).value" class="node-image-icon" alt="integration icon" />
                                    <span v-else class="node-icon">{{ parseIcon(nodeType.icon).value }}</span>
                                </div>
                                <div class="node-info">
                                    <div class="node-label-row">
                                        <span class="node-label">{{ nodeType.label }}</span>
                                        <span v-if="nodeType._integration" class="node-source-tag">
                                            {{ getIntegrationLabel(nodeType._integration) }}
                                        </span>
                                    </div>
                                    <small v-if="nodeType.description" class="node-desc">{{ nodeType.description }}</small>
                                </div>
                                <b-icon icon="grip-vertical" class="drag-handle-hint"/>
                            </div>
                        </div>
                    </div>

                    <!-- Actions Section -->
                    <div class="node-category" v-if="shouldShowCategory('operations') && filteredActionNodes.length > 0">
                        <div class="category-label">
                            <b-icon icon="gear" class="category-icon operations-icon"/>
                            <span>{{ $t('operations') }}</span>
                            <span class="node-count">{{ filteredActionNodes.length }}</span>
                        </div>
                        <div class="category-nodes">
                            <div 
                                v-for="nodeType in filteredActionNodes" 
                                :key="`action-${nodeType.type}-${nodeType.action}-${nodeType._integration && nodeType._integration.node_name ? nodeType._integration.node_name : ''}-${nodeType._integration && (nodeType._integration.integration_id || nodeType._integration.satellite_id) ? (nodeType._integration.integration_id || nodeType._integration.satellite_id) : ''}`"
                                class="draggable-node"
                                :class="[`${nodeType.type}-node`, `${nodeType.action}-node`, { 'integration-operation-node': isIntegrationOperationNode(nodeType) }]"
                                :data-type="nodeType.type"
                                :data-action="nodeType.action"
                                draggable="true"
                                @dragstart="onDragStart($event, nodeType)"
                                @dragend="onDragEnd"
                                :title="nodeType.description"
                            >
                                <div class="node-icon-wrapper" :class="{ 'has-integration-badge': nodeType._integration && nodeType._integration.integration_icon }">
                                    <b-icon v-if="parseIcon(nodeType.icon).type === 'b-icon'" :icon="parseIcon(nodeType.icon).value" class="node-icon" />
                                    <img v-else-if="parseIcon(nodeType.icon).type === 'image'" :src="parseIcon(nodeType.icon).value" class="node-image-icon" alt="integration icon" />
                                    <span v-else class="node-icon">{{ parseIcon(nodeType.icon).value }}</span>
                                    <img
                                        v-if="nodeType._integration && nodeType._integration.integration_icon && parseIcon(nodeType._integration.integration_icon).type === 'image'"
                                        :src="parseIcon(nodeType._integration.integration_icon).value"
                                        class="integration-logo-badge"
                                        alt=""
                                    />
                                </div>
                                <div class="node-info">
                                    <div class="node-label-row">
                                        <span class="node-label">{{ nodeType.label }}</span>
                                        <span v-if="nodeType._integration" class="node-source-tag">
                                            {{ getIntegrationLabel(nodeType._integration) }}
                                        </span>
                                    </div>
                                    <small v-if="nodeType.description" class="node-desc">{{ nodeType.description }}</small>
                                </div>
                                <b-icon icon="grip-vertical" class="drag-handle-hint"/>
                            </div>
                        </div>
                    </div>

                    <!-- Logic Section -->  
                    <div class="node-category" v-if="shouldShowCategory('logic') && filteredLogicNodes.length > 0">
                        <div class="category-label">
                            <b-icon icon="diagram-3" class="category-icon logic-icon"/>
                            <span>{{ $t('logic') }}</span>
                            <span class="node-count">{{ filteredLogicNodes.length }}</span>
                        </div>
                        <div class="category-nodes">
                            <div 
                                v-for="nodeType in filteredLogicNodes"  
                                :key="`logic-${nodeType.type}`"
                                class="draggable-node"
                                :class="[`${nodeType.type}-node`, `${nodeType.action}-node`]"
                                :data-type="nodeType.type"
                                :data-action="nodeType.action"
                                draggable="true"
                                @dragstart="onDragStart($event, nodeType)"
                                @dragend="onDragEnd"
                                :title="nodeType.description"
                            >
                                <div class="node-icon-wrapper">
                                    <b-icon v-if="parseIcon(nodeType.icon).type === 'b-icon'" :icon="parseIcon(nodeType.icon).value" class="node-icon" />
                                    <img v-else-if="parseIcon(nodeType.icon).type === 'image'" :src="parseIcon(nodeType.icon).value" class="node-image-icon" alt="integration icon" />
                                    <span v-else class="node-icon">{{ parseIcon(nodeType.icon).value }}</span>
                                </div>
                                <div class="node-info">
                                    <div class="node-label-row">
                                        <span class="node-label">{{ nodeType.label }}</span>
                                    </div>
                                    <small v-if="nodeType.description" class="node-desc">{{ nodeType.description }}</small>
                                </div>
                                <b-icon icon="grip-vertical" class="drag-handle-hint"/>
                            </div>
                        </div>
                    </div>
                    
                    <!-- No Results -->
                    <div class="no-results" v-if="hasNoFilteredResults">
                        <b-icon icon="search" class="no-results-icon"/>
                        <p class="no-results-title">{{ $t('noNodesFound') }}</p>
                        <p class="no-results-sub">{{ $t('tryDifferentSearch') }}</p>
                        <b-button 
                            variant="outline-secondary" 
                            size="sm" 
                            @click="clearFilters"
                        >
                            {{ $t('clearFilters') }}
                        </b-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Top Toolbar -->
        <div class="workflow-toolbar">
            <div class="toolbar-section">
                <b-button 
                    v-if="!readonly"
                    variant="outline-secondary" 
                    @click="openNodeSidebar"
                    v-b-tooltip.hover 
                    :title="$t('addNode')"
                    class="add-node-btn"
                >
                    <b-icon icon="plus"/> {{ $t('addNode') }}
                </b-button>
            </div>

            <div class="toolbar-section ml-auto">
                <div class="action-controls">
                    <b-button 
                        v-if="selectedNode && !readonly"
                        variant="outline-secondary" 
                        size="sm" 
                        @click="handleEditNode(selectedNode)"
                        v-b-tooltip.hover 
                        :title="$t('edit')"
                    >
                        <b-icon icon="pencil"/> {{ $t('edit') }}
                    </b-button>
                    <b-button 
                        v-if="selectedNode && !readonly"
                        variant="outline-danger" 
                        size="sm" 
                        @click="handleRemoveNode(selectedNode)"
                        v-b-tooltip.hover 
                        :title="$t('delete')"
                    >
                        <b-icon icon="trash"/>
                    </b-button>
                    
                    <!-- History Controls -->
                    <template v-if="!readonly">
                        <div class="toolbar-divider"></div>
                        <b-button 
                            variant="outline-secondary" 
                            size="sm" 
                            @click="undo"
                            :disabled="!canUndo"
                            v-b-tooltip.hover 
                            title="Undo (Ctrl+Z)"
                            class="history-btn"
                        >
                            <b-icon icon="arrow-counterclockwise"/>
                        </b-button>
                        <b-button 
                            variant="outline-secondary" 
                            size="sm" 
                            @click="redo"
                            :disabled="!canRedo"
                            v-b-tooltip.hover 
                            title="Redo (Ctrl+Y)"
                            class="history-btn"
                        >
                            <b-icon icon="arrow-clockwise"/>
                        </b-button>
                        <b-button 
                            variant="outline-info" 
                            size="sm" 
                            @click="openHistoryModal"
                            v-b-tooltip.hover 
                            title="View History"
                            class="history-btn"
                        >
                            <b-icon icon="clock-history"/>
                        </b-button>
                    </template>
                    
                    <div class="toolbar-divider"></div>
                    <b-button 
                        variant="outline-secondary" 
                        size="sm" 
                        @click="isFullscreen = !isFullscreen"
                        v-b-tooltip.hover 
                        :title="isFullscreen ? $t('exitFullscreen') : $t('fullscreen')"
                    >
                        <b-icon :icon="isFullscreen ? 'fullscreen-exit' : 'fullscreen'"/>
                    </b-button>
                </div>
            </div>
        </div>
        <!-- Main Workflow Canvas -->
        <div class="workflow-container" @click="handleContainerClick">
            <div 
                class="workflow-canvas" 
                v-if="nodes && connections"
                @drop="onCanvasDrop"
                @dragover="onCanvasDragOver"
                @dragenter="onCanvasDragEnter"
                @dragleave="onCanvasDragLeave"
                :class="{ 'drag-over': isDragOver }"
            >
                <FlowChart
                    :nodes="nodes"
                    :connections="connections"
                    @editnode="handleEditNode"
                    @dblclick="handleDblClick"
                    @editconnection="handleEditConnection"
                    @playnode="handlePlayNode"
                    @save="handleChartSave"
                    @dragend="dragend"
                    :createConnection="createConnection"
                    ref="chart"
                    :width="'100%'"
                    :render="enhancedRender"
                    :key="version"
                    :readonly="readonly"
                    class="flowchart-canvas"
                    >
                </FlowChart>
                
                <!-- Grid Background -->
                <div class="canvas-grid"></div>
                
                <!-- Drop Zone Indicator -->
                <div class="drop-zone-indicator" v-show="isDragOver">
                    <div class="drop-zone-content">
                        <b-icon icon="plus-circle" class="drop-icon"/>
                        <h5>{{ $t('dropToAdd') }}</h5>
                        <p>{{ $t('dropNodeHere') }}</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Node Drawer -->
        <div class="node-drawer" :class="{ open: newNode.modal }" @click.self="closeAddNodeDrawer">
            <div class="drawer-content">
                <div class="drawer-header">
                    <h5>{{ $t('addNode') }}</h5>
                    <b-button variant="link" size="sm" @click="closeAddNodeDrawer" class="close-btn">
                        <b-icon icon="x"/>
                    </b-button>
                </div>
                <div class="drawer-body">
                    <SelectField name="type" :label="this.$t('type')" v-model="newNode.data.type" @error="fieldError"
                        :items="['event', 'operation', 'condition', 'each', 'switch', 'expression', 'recycle']"
                        :reduce="(o) => o"
                        :rules="{required: true}"
                    />

                    <div v-if="newNode.data.type == 'event'">
                        <b-form-group v-if="schema"
                            :label="$t('schema')"
                            label-for="event-schema"
                            :label-cols="2"
                            :horizontal="true"
                            :description="$t('schemaForEvent')">
                            <v-select
                                id="event-schema"
                                v-model="newNode.data.schema"
                                :options="availableSchemas"
                                :placeholder="schema || 'warehouse'"
                                taggable
                            />
                        </b-form-group>

                        <v-select name="name" label="label" v-model="newNodeName" @error="fieldError"
                            :options="eventNamesFormated"
                            taggable
                            :reduce="eventNameReducer"
                        />
                    </div>

                    <div v-if="newNode.data.type == 'event'">
                        <div v-if="newNode.data.name == 'Recycle'">
                            <SelectField name="timer" :label="this.$t('timer')" v-model="newNode.data.data.timer" @error="fieldError"
                                :items="timers"
                                :rules="{required: true}"
                                v-if="newNode.data.name == 'Recycle'"
                                :description="this.$t('eventTriggered')"
                            />
                            <Helptip helpType="icon" tipId="event.recycle.ruleset" size="sm">
                                <CodeEditor
                                    v-model="newNode.data.data.expression"
                                    :label="this.$t('ruleset')"
                                    :label-cols="2"
                                    :horizontal="true"
                                    :controls='[""]'
                                    :vars="[{name: 'ruleset', usage: `{}`}]"
                                    :libs="rulesetLibs"
                                    :errors="recycleExprErrors"
                                />
                            </Helptip>
                        </div>

                        <b-form-group
                            :label="this.$t('paused')"
                            label-for="module"
                            :label-cols="2"
                            label-cols-sm="2"
                            class="mt-4"
                            :horizontal="false">
                                <c-switch class="mx-1" color="success" variant="pill" :label="true" :dataOn="$t('yes')" :dataOff="$t('no')" v-model="newNode.data.paused"/>
                        </b-form-group>
                    </div>

                    <template v-if="newNode.data.type == 'action'">
                        <InputField name="name" :label="this.$t('name')" v-model="newNodeName" @error="fieldError"
                            :rules="{required:true, length: {min: 2}}"
                        />

                        <SelectField name="action" :label="this.$t('action')" v-model="newNode.data.action" @error="fieldError"
                            :items="actions"
                            :rules="{required: true}"
                        />
                    </template>

                     <template v-if="newNode.data.type == 'condition'">
                        <InputField name="name" :label="this.$t('name')" v-model="newNodeName" @error="fieldError"
                            :rules="{required:true, length: {min: 2}}"
                        />

                        <b-form-group
                            :label-cols="0"
                            :horizontal="false"
                        >
                            <ConditionExpression v-model="newNode.data.data" />
                        </b-form-group>
                    </template>

                    <template v-if="newNode.data.type == 'switch'">
                        <InputField name="name" :label="this.$t('name')" v-model="newNodeName" @error="fieldError"
                            :rules="{required:true, length: {min: 2}}"
                        />

                        <b-form-group
                            :label-cols="0"
                            :horizontal="false"
                        >
                            <SwitchExpression v-model="newNode.data.data" />
                        </b-form-group>
                    </template>

                    <b-form-group
                        :label="this.$t('notification')"
                        :label-cols="2"
                        :horizontal="false"
                        v-if="newNode.data.action == 'platform_notification' && newNode.data.type == 'operation'"
                    >
                        <PlatformNotifications v-model="newNode.data.data" />
                    </b-form-group>

                    <b-form-group
                        :label="this.$t('notification')"
                        :label-cols="2"
                        :horizontal="false"
                        v-if="newNode.data.action == 'push_notification'  && newNode.data.type == 'operation'"
                    >
                        <PushNotification v-model="newNode.data.data" />
                    </b-form-group>

                    <b-form-group
                        :label="this.$t('request')"
                        :label-cols="2"
                        :horizontal="false"
                        v-if="newNode.data.action == 'http_request'  && newNode.data.type == 'operation'"
                    >
                        <HttpRequest v-model="newNode.data.data" />
                    </b-form-group>

                    <b-form-group
                        :label-cols="0"
                        :horizontal="false"
                        v-if="newNode.data.action == 'expression'  && newNode.data.type == 'operation'"
                    >
                        <Expression v-model="newNode.data.data" :nodeName="newNodeName"/>
                    </b-form-group>

                    <b-form-group
                        :label-cols="0"
                        :horizontal="false"
                        v-if="newNode.data.action == 'component'  && newNode.data.type == 'operation'"
                    >
                        <ComponentExpression v-model="newNode.data.data" />
                    </b-form-group>

                    <template v-if="newNode.data.action == 'spark' && newNode.data.type == 'operation' && isSparkActive">
                        <Spark v-model="newNode.data.data" />
                    </template>
                </div>
                <div class="drawer-footer">
                    <b-button variant="outline-secondary" @click="closeAddNodeDrawer">
                        {{ $t('cancel') }}
                    </b-button>
                    <b-button variant="primary" @click="newNodeAdd">
                        {{ $t('add') }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- Edit Node Popover on Canvas -->
        <div v-if="editNode.modal" class="node-edit-popover" :style="{ ...dragPosition, width: popoverWidth + 'px', height: popoverHeight + 'px' }">
            <div class="popover-header" @mousedown="startDrag" @dblclick="resetPopoverSize" style="cursor: move;">
                <h6>{{ $t('editNode') }}</h6>
                <div class="d-flex align-items-center">
                    <b-button v-if="editNode.data && editNode.data.type === 'event'" variant="link" size="sm" @click.stop="handlePlayNode(editNode.data)" class="mr-1" v-b-tooltip.hover title="Play this node">
                        <b-icon icon="play-circle" variant="success"/>
                    </b-button>
                    <b-button variant="link" size="sm" @click="closeEditNodeDrawer" class="close-btn">
                        <b-icon icon="x"/>
                    </b-button>
                </div>
            </div>
            <div class="popover-body">
                <div v-if="editNode.data.type == 'event'">
                  <div v-if="editNode.data.name == 'Recycle'">
                        <SelectField name="timer" :label="this.$t('timer')" v-model="editNode.data.data.timer" @error="fieldError"
                            :items="timers"
                            :rules="{required: true}"
                            v-if="editNode.data.name == 'Recycle'"
                            :description="this.$t('eventTriggeredInTime')"
                        />
                    </div>

                    <b-form-group v-if="schema && editNode.data.action !== 'custom' && editNode.data.action !== 'recycle' && editNode.data.name !== 'Recycle'"
                        :label="$t('schema')"
                        label-for="edit-event-schema"
                        label-size="sm"
                        :description="$t('schemaForEvent')">
                        <v-select
                            id="edit-event-schema"
                            v-model="editNode.data.schema"
                            :options="availableSchemas"
                            taggable
                        />
                    </b-form-group>

                    <div v-if="editNode.data.action == 'custom'">
                        <InputField name="name" :label="this.$t('name')" v-model="editNodeName" @error="fieldError"
                            :rules="{required:true, length: {min: 2}}"
                        />
                    </div>

                     <b-form-group
                        :label="this.$t('paused')"
                        label-for="module"
                        label-size="sm"
                        class="mt-3 mb-0">
                        <c-switch class="mx-1" color="success" variant="pill" :label="true" dataOn="Yes" dataOff="No" v-model="editNode.data.paused"/>
                    </b-form-group>

                    <template v-if="editNode.data.customSettingsView && customSettingsViewComponents[editNode.data.customSettingsView]">
                        <component
                            :is="customSettingsViewComponents[editNode.data.customSettingsView]"
                            v-model="editNode.data.data.settings"
                            :settingsDef="editNode.data.eventSettingsDef"
                        />
                    </template>
                    <EventSettingsNode
                        v-else-if="editNode.data.eventSettingsDef && editNode.data.eventSettingsDef.length > 0"
                        :settingsDef="editNode.data.eventSettingsDef"
                        v-model="editNode.data.data.settings"
                    />

                    <div v-if="editNode.data.outputs && editNode.data.outputs.length > 0" class="event-outputs-section mt-3">
                        <div class="event-outputs-header" @click="toggleEventOutputs">
                            <b-icon :icon="showEventOutputs ? 'chevron-down' : 'chevron-right'" class="event-outputs-chevron"/>
                            <span>{{ $t('availableVariables') }}</span>
                            <span class="event-outputs-count">{{ editNode.data.outputs.length }}</span>
                        </div>
                        <div v-if="showEventOutputs" class="event-outputs-list">
                            <div v-for="output in editNode.data.outputs" :key="output.name" class="event-output-item">
                                <div class="event-output-header">
                                    <span class="event-output-name">{{ output.name }}</span>
                                    <span class="event-output-type">{{ output.type }}</span>
                                </div>
                                <div v-if="output.description" class="event-output-desc">{{ output.description }}</div>
                                <div v-if="output.example !== undefined && output.example !== null" class="event-output-example">
                                    <span class="event-output-example-label">e.g.</span>
                                    <span class="event-output-example-value">{{ formatOutputExample(output.example) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <template v-if="editNode.data.type == 'condition'">
                    <InputField name="name" :label="this.$t('name')" v-model="editNodeName" @error="fieldError"
                        :rules="{required:true, length: {min: 2}}"
                    />

                    <b-form-group>
                        <ConditionExpression v-model="editNode.data.data" :withButton="false"/>
                    </b-form-group>
                </template>

                <template v-if="editNode.data.type == 'switch'">
                    <InputField name="name" :label="this.$t('name')" v-model="editNodeName" @error="fieldError"
                        :rules="{required:true, length: {min: 2}}"
                    />

                    <b-form-group>
                        <SwitchExpression v-model="editNode.data.data" />
                    </b-form-group>
                </template>

                <template v-if="editNode.data.type == 'operation'">
                    <InputField name="name" :label="this.$t('name')" v-model="editNodeName" @error="fieldError"
                        :rules="{required:true, length: {min: 2}}"
                    />

                    <b-form-group
                        v-if="editNode.data.action == 'expression'"
                    >
                        <Expression v-model="editNode.data.data" :nodeName="editNode.data.name" :editorHeight="popoverHeight - 200"/>
                    </b-form-group>

                    <template v-if="editNode.data.action == 'spark' && isSparkActive">
                        <Spark v-model="editNode.data.data" />
                    </template>

                    <template v-if="editNode.data.action == 'integration'">
                        <template v-if="editNode.data.data._integration && editNode.data.data._integration.customView">
                            <component
                                :is="customViewComponents[editNode.data.data._integration.customView]"
                                v-model="editNode.data.data.inputs"
                            />
                            <div v-if="editNode.data.data._integration.outputs && editNode.data.data._integration.outputs.length > 0" style="margin-top: 1.5rem;">
                                <h6 style="font-size:0.85rem;font-weight:600;margin-bottom:1rem;color:#6c757d;text-transform:uppercase;letter-spacing:0.5px;">{{ $t('outputs') }}</h6>
                                <div style="display:flex;flex-direction:column;gap:0.5rem;">
                                    <div v-for="output in editNode.data.data._integration.outputs" :key="output.name" style="display:flex;flex-direction:column;padding:0.5rem;background:rgba(0,0,0,0.05);border-radius:0.25rem;border-left:3px solid #10b981;">
                                        <span style="font-weight:600;font-size:0.85rem;">{{ output.name }}</span>
                                        <span style="font-size:0.75rem;color:#6c757d;font-family:monospace;">{{ output.type }}</span>
                                        <small v-if="output.description" style="font-size:0.75rem;color:#6c757d;margin-top:0.25rem;">{{ $translateLabel(output.description, output.name) }}</small>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <IntegrationNode v-else v-model="editNode.data.data" />
                    </template>
                </template>
            </div>
            <div class="popover-footer">
                <b-button size="sm" variant="outline-secondary" @click="closeEditNodeDrawer">
                    {{ $t('cancel') }}
                </b-button>
                <b-button size="sm" variant="primary" @click="saveNodeEdit">
                    {{ $t('save') }}
                </b-button>
            </div>
            <div class="popover-resize-handle" @mousedown="startResize"></div>
        </div>

        <main-dialog v-model="editConnectionModal" hide-footer>
            <InputField name="path" :label="this.$t('path')" v-model="editConnectionName" @error="fieldError"
                :rules="{required:true, length: {min: 2}}"
            />
        </main-dialog>

        <!-- History Modal -->
        <main-dialog 
            v-model="historyModal" 
            title="Workflow History" 
            size="lg"
            hide-footer
            body-class="p-0"
        >
            <div class="history-modal-content">
                <div class="history-header">
                    <div class="history-stats">
                        <span class="badge badge-secondary">{{ history.length }} versions</span>
                        <span v-if="currentHistoryEntry" class="badge badge-primary ml-2">
                            Current: {{ formatHistoryTimestamp(currentHistoryEntry.timestamp) }}
                        </span>
                    </div>
                    <b-button 
                        variant="outline-danger" 
                        size="sm" 
                        @click="clearHistory"
                        v-if="history.length > 0"
                    >
                        Clear History
                    </b-button>
                </div>
                
                <div class="history-list">
                    <div 
                        v-for="(entry, index) in history.slice().reverse()" 
                        :key="entry.id"
                        class="history-entry"
                        :class="{ 'current': index === (history.length - 1 - historyIndex) }"
                        @click="restoreToHistoryEntry(entry)"
                    >
                        <div class="history-entry-header">
                            <div class="history-action">{{ entry.action }}</div>
                            <div class="history-timestamp">{{ formatHistoryTimestamp(entry.timestamp) }}</div>
                        </div>
                        <div class="history-details">
                            <span class="detail-item">
                                <b-icon icon="diagram-3"/> {{ entry.nodeCount }} nodes
                            </span>
                            <span class="detail-item">
                                <b-icon icon="arrow-left-right"/> {{ entry.connectionCount }} connections
                            </span>
                        </div>
                    </div>
                </div>
                
                <div v-if="history.length === 0" class="empty-state">
                    <b-icon icon="clock-history" font-scale="3" class="text-muted"/>
                    <p class="text-muted mt-3">No history available</p>
                    <small class="text-muted">Changes will be automatically tracked</small>
                </div>
            </div>
        </main-dialog>

        <Drawer @close="toggleResultsSidebar" align="down" :mask="false" :z-index="10500">
            <div class="p-2" v-if="inspectResult && resultsSidebar">
                <tree-view :data="inspectResult.State.Results" :options="{maxDepth: 2, rootObjectKey:'results'}"></tree-view>
                <tree-view :data="inspectResult.data" :options="{maxDepth: 2, rootObjectKey:'data'}"></tree-view>
            </div>
        </Drawer>
        

        <!-- Play Node Sidebar -->
        <div v-if="playNodeModal" class="node-edit-popover play-node-popover" :style="{ ...playDragPosition, width: playPopoverWidth + 'px', height: playPopoverHeight + 'px' }">
            <div class="popover-header" @mousedown="startPlayDrag" style="cursor: move;">
                <h6 class="d-flex align-items-center mb-0">
                    <b-icon icon="play-circle" class="mr-2" variant="success"/>
                    {{ playNodeTarget ? playNodeTarget.name || playNodeTarget.label || 'Node' : 'Play Node' }}
                </h6>
                <div class="d-flex align-items-center">
                    <b-button variant="success" size="sm" @click="executePlayNode()" :disabled="playNodeRunning" class="mr-2">
                        <b-spinner v-if="playNodeRunning" small class="mr-1"/>
                        <b-icon v-else icon="play-fill" class="mr-1"/>
                        {{ playNodeRunning ? 'Running...' : 'Execute' }}
                    </b-button>
                    <small v-if="playNodeExecutionTime" class="text-muted mr-2">{{ playNodeExecutionTime }}ms</small>
                    <b-button variant="link" size="sm" @click="closePlayNode" class="close-btn">
                        <b-icon icon="x"/>
                    </b-button>
                </div>
            </div>
            <div class="popover-body" style="overflow-y: auto; flex: 1;">
                <div class="p-3">
                    <div class="mb-3">
                        <label class="font-weight-bold small">Mock Input Data (JSON)</label>
                        <CodeEditor
                            v-model="playNodeInput"
                            :controls='[""]'
                            placeholder="{}"
                            @onInput="onPlayNodeInput"
                            :errors="playNodeInputErrors"
                            :withButton="false"
                        />
                    </div>

                    <div v-if="playNodeStatus" class="mt-3">
                        <div class="d-flex align-items-center mb-2">
                            <label class="font-weight-bold small mb-0">Execution</label>
                            <b-badge :variant="getNodeStatusVariant(playNodeStatus)" class="ml-2">{{ playNodeStatus }}</b-badge>
                            <small v-if="playNodeExecutionUUID" class="ml-auto text-muted" style="font-size: 0.7em">{{ playNodeExecutionUUID.substring(0, 8) }}...</small>
                        </div>
                        <small v-if="playNodeResults.length > 0 && !playNodeRunning" class="text-muted">Double-click on highlighted nodes in the canvas to view their execution details.</small>
                    </div>
                </div>
            </div>
            <div class="popover-resize-handle" @mousedown="startPlayResize"></div>
        </div>

        <!-- Node execution detail popover -->
        <div v-if="playNodeDetail" class="node-edit-popover node-detail-popover" :style="{ ...detailDragPosition, width: detailPopoverWidth + 'px', height: detailPopoverHeight + 'px' }">
            <div class="popover-header" @mousedown="startDetailDrag" style="cursor: move;">
                <h6 class="d-flex align-items-center mb-0">
                    <b-badge :variant="getNodeStatusVariant(nodeDetailState.status)" class="mr-2">{{ nodeDetailState.status }}</b-badge>
                    {{ playNodeDetail.name }}
                    <small class="ml-2 text-muted">({{ playNodeDetail.type }})</small>
                </h6>
                <b-button variant="link" size="sm" @click="playNodeDetail = null; detailSearchQuery = ''; detailPopoverX = 0; detailPopoverY = 0;" class="close-btn">
                    <b-icon icon="x"/>
                </b-button>
            </div>
            <div class="detail-search-bar px-3 pt-2">
                <b-input-group size="sm">
                    <b-form-input
                        v-model="detailSearchQuery"
                        placeholder="Search keys or values..."
                    />
                    <b-input-group-append>
                        <b-button variant="outline-secondary" @click="detailSearchQuery = ''" v-if="detailSearchQuery">
                            <b-icon icon="x"/>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </div>
            <div class="popover-body" style="overflow-y: auto; flex: 1;">
                <div class="p-3">
                    <div v-if="nodeDetailState.input" class="mb-3">
                        <label class="font-weight-bold small">Input</label>
                        <div class="json-viewer-box">
                            <JsonTreeView :data="nodeDetailState.input" :maxDepth="4" :readOnly="true" :searchQuery="detailSearchQuery"/>
                        </div>
                    </div>

                    <div v-if="nodeDetailState.results" class="mb-3">
                        <label class="font-weight-bold small">Results</label>
                        <div class="json-viewer-box">
                            <JsonTreeView :data="nodeDetailState.results" :maxDepth="4" :readOnly="true" :searchQuery="detailSearchQuery"/>
                        </div>
                    </div>

                    <div v-if="nodeDetailState.error" class="mb-3">
                        <label class="font-weight-bold small text-danger">Error</label>
                        <div class="text-danger small p-2 rounded" style="background: rgba(239,68,68,0.1);">
                            {{ nodeDetailState.error }}
                        </div>
                    </div>

                    <div v-if="nodeDetailState.starttime || nodeDetailState.endtime" class="small text-muted">
                        <div v-if="nodeDetailState.starttime">Started: {{ new Date(nodeDetailState.starttime).toLocaleTimeString() }}</div>
                        <div v-if="nodeDetailState.endtime">Ended: {{ new Date(nodeDetailState.endtime).toLocaleTimeString() }}</div>
                    </div>
                </div>
            </div>
            <div class="popover-resize-handle" @mousedown="startDetailResize"></div>
        </div>

        <b-sidebar v-model="dryRunInputModal" right backdrop width="500px" z-index="1040">
            <template v-if="isDryRunCustomEvent">
                <div class="p-2">
                    <CodeEditor
                        v-model="dryRunInput"
                        :controls='[""]'
                        placeholder="{}"
                        @onInput="onDryInput"
                        :errors="dryRunInputErrors"
                        :withButton="false"
                    />
                </div>
            </template>

            <template v-else>
                <CreateModel :id="uuid" @modelChanged="modelChanged" entrypoint="default" :settings="{container: {cols:12,md:12}}">
                    <template #controls>
                        <span></span>
                    </template>
                </CreateModel>
            </template>

            <template #footer>
                <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
                    <b-button variant="success" @click="dryRun()"><b-icon icon="play" /></b-button>
                </div>
            </template>
        </b-sidebar>
    </div>
</template>

<style scoped>
/* Main workflow builder container */
.workflow-builder {
    background: var(--bg-card);
    min-height: 100vh;
    display: flex;
    flex-direction: column;
}

.workflow-builder.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 99999;
    padding-top: 15px;
}

/* Enhanced Toolbar */
.workflow-toolbar {
    background: var(--form-bg);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid var(--border-default);
    padding: 1rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.toolbar-section {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.toolbar-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.toolbar-title {
    color: #ecf0f1;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
    opacity: 0.8;
}

/* Node Palette */
.node-palette {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.node-palette-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding: 0.75rem 0.5rem;
    background: var(--border-default);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: 70px;
    position: relative;
}

.node-palette-item:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: var(--border-hover);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.node-palette-item.event {
    border-left: 3px solid #28a745;
}

.node-palette-item.operation {
    border-left: 3px solid #7c3aed;
}

.node-palette-item.condition {
    border-left: 3px solid #ffc107;
}

.node-palette-item.switch {
    border-left: 3px solid #6f42c1;
}

.node-palette-item.each {
    border-left: 3px solid #fd7e14;
}

.node-icon {
    font-size: 1.2rem;
    color: #ecf0f1;
}

.node-label {
    font-size: 0.7rem;
    color: #bdc3c7;
    text-align: center;
    font-weight: 500;
}


/* Add Node Button */
.add-node-btn {
    background: var(--bg-card) !important;
    border-color: var(--border-hover) !important;
    color: var(--text-primary) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.add-node-btn:hover {
    background: var(--form-bg) !important;
    border-color: var(--border-hover) !important;
    color: var(--text-primary) !important;
}

/* Action Controls */
.action-controls {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

/* Main Container */
.workflow-container {
    flex: 1;
    display: flex;
    position: relative;
    overflow: hidden;
}

.workflow-canvas {
    flex: 1;
    position: relative;
    background: var(--bg-card);
    background-image: 
        linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 20px 20px;
    overflow: hidden;
}

.flowchart-canvas {
    width: 100% !important;
    height: 100% !important;
    position: relative;
    z-index: 1;
}

.canvas-grid {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;
}



/* Legacy styles for compatibility */
.flowchart {
    width: 100% !important;
}

.event-modal .modal-dialog {
    max-width: 100%;
    margin: 0;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100vh;
    display: flex;
    position: fixed;
    z-index: 100000;
}

.dryRunResults {
    position: fixed;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    z-index: 9999;
    background-color: rgba(0, 0, 0, 0.7)
}

.play-node-result {
    background: var(--bg-card-secondary, #2a2f3a);
    border-radius: 6px;
    padding: 10px;
    border: 1px solid var(--border-default, #3a404b);
}

.json-viewer-box {
    border: 1px solid var(--border-default, #3a404b);
    border-radius: 6px;
    padding: 12px;
    background: var(--bg-card-secondary, #2a2f3a);
    max-height: 300px;
    overflow-y: auto;
}

/* Offset sidebars to clear app header (55px) and footer (50px) */
::v-deep .b-sidebar {
    top: 55px !important;
    height: calc(100vh - 55px - 50px) !important;
}
::v-deep .b-sidebar-backdrop {
    top: 55px !important;
    height: calc(100vh - 55px) !important;
}

/* History System Styles */
.toolbar-divider {
    width: 1px;
    height: 20px;
    background-color: var(--bg-card-secondary);
    margin: 0 8px;
}

.history-btn {
    transition: all 0.2s ease;
}

.history-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.history-btn:disabled {
    opacity: 0.4;
}

.history-modal-content {
    max-height: 600px;
}

.history-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #444;
    background-color: var(--bg-card);
}

.history-stats {
    display: flex;
    align-items: center;
}

.history-list {
    max-height: 400px;
    overflow-y: auto;
}

.history-entry {
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-default);
    cursor: pointer;
    transition: background-color 0.2s ease;
    background-color: var(--bg-main-end);
}

.history-entry:hover {
    background-color: var(--bg-card);
}

.history-entry.current {
    background-color: #0d6efd;
    color: var(--text-primary);
}

.history-entry.current:hover {
    background-color: #0b5ed7;
}

.history-entry-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
}

.history-action {
    font-weight: 500;
    font-size: 14px;
}

.history-timestamp {
    font-size: 12px;
    opacity: 0.7;
}

.history-details {
    display: flex;
    gap: 16px;
    font-size: 12px;
    opacity: 0.8;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

.empty-state {
    padding: 40px;
    text-align: center;
}

/* Responsive Design */
@media (max-width: 768px) {
    .workflow-toolbar {
        flex-direction: column;
        align-items: stretch;
    }
    
    .toolbar-section {
        justify-content: center;
    }
    
    .node-palette {
        justify-content: center;
    }
}

/* Dark theme adjustments */
.workflow-builder .btn-outline-secondary {
    color: var(--text-primary) !important;
    border-color: var(--border-hover) !important;
    background: var(--bg-card) !important;
}

.workflow-builder .btn-outline-secondary:hover {
    color: var(--text-primary) !important;
    background: var(--form-bg) !important;
    border-color: var(--border-hover) !important;
}

.workflow-builder .btn-outline-danger {
    color: #e74c3c !important;
    border-color: #e74c3c !important;
    background: var(--bg-card) !important;
}

.workflow-builder .btn-outline-danger:hover {
    color: var(--text-primary) !important;
    background: #e74c3c !important;
    border-color: #e74c3c !important;
}

/* Right Sidebar Styles */
.workflow-sidebar-right {
    position: fixed;
    right: -320px;
    top: 55px;
    bottom: 50px;
    width: 320px;
    background: var(--bg-main-end, #1a1d24);
    border-left: 1px solid var(--border-default);
    z-index: 11000;
    display: flex;
    flex-direction: column;
    transition: right 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.5);
    pointer-events: auto;
}

.workflow-builder.fullscreen .workflow-sidebar-right {
    top: 0;
    bottom: 0;
}

.workflow-sidebar-right.open {
    right: 0;
}

/* Sidebar Header */
.sidebar-header {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-default);
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--bg-card);
    flex-shrink: 0;
}

.sidebar-header-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.sidebar-header-icon {
    color: #4fd1c7;
    font-size: 1rem;
}

.sidebar-title {
    color: var(--text-primary);
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.2px;
}

.close-btn {
    color: var(--text-secondary) !important;
    padding: 0.2rem !important;
    line-height: 1 !important;
    opacity: 0.7;
}

.close-btn:hover {
    color: var(--text-primary) !important;
    opacity: 1;
}

/* Search */
.sidebar-search {
    padding: 0.75rem 0.75rem 0;
    flex-shrink: 0;
}

.search-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon-left {
    position: absolute;
    left: 0.65rem;
    color: var(--text-secondary);
    font-size: 0.8rem;
    pointer-events: none;
    z-index: 1;
}

.node-search-input {
    background: var(--bg-card) !important;
    border-color: var(--border-default) !important;
    color: var(--text-primary) !important;
    padding-left: 2rem !important;
    padding-right: 2rem !important;
    font-size: 0.8rem !important;
    border-radius: 6px !important;
}

.node-search-input:focus {
    border-color: #4fd1c7 !important;
    box-shadow: 0 0 0 2px rgba(79, 209, 199, 0.15) !important;
}

.search-clear-btn {
    position: absolute;
    right: 0.25rem;
    color: var(--text-secondary) !important;
    padding: 0.2rem 0.4rem !important;
    line-height: 1 !important;
    font-size: 0.75rem;
}

.search-clear-btn:hover {
    color: var(--text-primary) !important;
}

/* Category Tabs */
.sidebar-tabs {
    display: flex;
    padding: 0.5rem 0.75rem;
    gap: 0.25rem;
    flex-shrink: 0;
    overflow-x: auto;
    scrollbar-width: none;
}

.sidebar-tabs::-webkit-scrollbar {
    display: none;
}

.sidebar-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.25rem 0.6rem;
    border-radius: 20px;
    border: 1px solid var(--border-default);
    background: transparent;
    color: var(--text-secondary);
    font-size: 0.72rem;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s ease;
    line-height: 1.4;
}

.sidebar-tab svg {
    font-size: 0.7rem;
    flex-shrink: 0;
}

.sidebar-tab:hover {
    border-color: var(--border-hover);
    color: var(--text-primary);
    background: var(--bg-card);
}

.sidebar-tab.active {
    background: rgba(79, 209, 199, 0.15);
    border-color: rgba(79, 209, 199, 0.5);
    color: #4fd1c7;
}

/* Sidebar Content */
.sidebar-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem 0 1rem;
    scrollbar-width: thin;
    scrollbar-color: var(--border-default) transparent;
}

.sidebar-content::-webkit-scrollbar {
    width: 4px;
}

.sidebar-content::-webkit-scrollbar-thumb {
    background: var(--border-default);
    border-radius: 2px;
}

/* Node Category */
.node-category {
    margin-bottom: 0.5rem;
}

.category-label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.6rem 1rem 0.4rem;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: var(--text-secondary);
}

.category-icon {
    font-size: 0.75rem;
    flex-shrink: 0;
}

.category-icon.events-icon { color: #4fd1c7; }
.category-icon.operations-icon { color: #7c3aed; }
.category-icon.logic-icon { color: #f59e0b; }

.node-count {
    margin-left: auto;
    background: var(--border-default);
    color: var(--text-secondary);
    font-size: 0.65rem;
    font-weight: 600;
    padding: 0.1rem 0.4rem;
    border-radius: 10px;
    min-width: 18px;
    text-align: center;
}

.category-nodes {
    padding: 0 0.5rem;
}

/* Draggable Nodes */
.draggable-node {
    display: flex;
    align-items: center;
    padding: 0.55rem 0.65rem;
    margin: 0.2rem 0;
    background: var(--bg-card);
    border: 1px solid var(--border-default);
    border-radius: 8px;
    cursor: grab;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    user-select: none;
    gap: 0.6rem;
}

.draggable-node .node-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    line-height: 0;
    border-radius: 8px;
    font-size: 1.1rem;
    background: rgba(124, 58, 237, 0.2);
    color: #7c3aed;
}

.node-image-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
    object-position: center;
    margin: 0 auto;
    flex-shrink: 0;
    display: block;
}

.draggable-node .node-icon-wrapper .node-icon {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-size: 15px;
    margin: 0;
}

.draggable-node .node-icon-wrapper svg.node-icon {
    width: 18px;
    height: 18px;
    display: block;
}

.draggable-node:hover {
    background: var(--form-bg);
    border-color: var(--border-hover);
    transform: translateX(-2px);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.25), inset -2px 0 0 rgba(79, 209, 199, 0.25);
}

.draggable-node:active {
    cursor: grabbing;
    transform: translateX(0);
    box-shadow: none;
}

/* Drag handle hint icon */
.drag-handle-hint {
    flex-shrink: 0;
    font-size: 0.85rem;
    color: var(--border-default);
    opacity: 0;
    transition: opacity 0.15s ease;
}

.draggable-node:hover .drag-handle-hint {
    opacity: 0.6;
}

/* Node info */
.node-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

.node-label-row {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    flex-wrap: wrap;
}

.node-label {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.82rem;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
}

.node-source-tag {
    flex-shrink: 0;
    font-size: 0.62rem;
    padding: 0.08rem 0.35rem;
    background: rgba(79, 209, 199, 0.12);
    color: #4fd1c7;
    border-radius: 4px;
    border: 1px solid rgba(79, 209, 199, 0.25);
    font-weight: 500;
    white-space: nowrap;
}

.node-desc {
    color: var(--text-secondary);
    font-size: 0.71rem;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* Integration node accent */
.draggable-node.integration-operation-node {
    border-left: 2px solid rgba(14, 165, 233, 0.5);
}

.draggable-node.integration-operation-node .node-icon-wrapper {
    background: rgba(14, 165, 233, 0.2) !important;
    color: #0ea5e9 !important;
    position: relative;
}

.node-icon-wrapper.has-integration-badge {
    position: relative;
}

.node-icon-wrapper.has-integration-badge .node-icon,
.node-icon-wrapper.has-integration-badge .node-image-icon {
    visibility: hidden;
}

.integration-logo-badge {
    position: absolute;
    top: 0;
    left: 0;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    object-fit: contain;
    padding: 6px;
}

/* Node type icon colours */
.draggable-node.event-node .node-icon-wrapper,
.draggable-node[data-type="event"] .node-icon-wrapper {
    background: rgba(79, 209, 199, 0.2) !important;
    color: #4fd1c7 !important;
}

.draggable-node.operation-node .node-icon-wrapper,
.draggable-node[data-type="operation"] .node-icon-wrapper {
    background: rgba(124, 58, 237, 0.2) !important;
    color: #7c3aed !important;
}

.draggable-node.condition-node .node-icon-wrapper,
.draggable-node[data-type="condition"] .node-icon-wrapper {
    background: rgba(245, 158, 11, 0.2) !important;
    color: #f59e0b !important;
}

.draggable-node.switch-node .node-icon-wrapper,
.draggable-node[data-type="switch"] .node-icon-wrapper {
    background: rgba(59, 130, 246, 0.2) !important;
    color: #3b82f6 !important;
}

.draggable-node.each-node .node-icon-wrapper,
.draggable-node[data-type="each"] .node-icon-wrapper {
    background: rgba(16, 185, 129, 0.2) !important;
    color: #10b981 !important;
}

.draggable-node.expression-node .node-icon-wrapper {
    background: rgba(139, 92, 246, 0.2) !important;
    color: #8b5cf6 !important;
}

.draggable-node.recycle-node .node-icon-wrapper,
.draggable-node[data-type="recycle"] .node-icon-wrapper {
    background: rgba(6, 182, 212, 0.2) !important;
    color: #06b6d4 !important;
}

.draggable-node.custom-node .node-icon-wrapper {
    background: rgba(79, 209, 199, 0.2);
    color: #4fd1c7;
}

.draggable-node.create-node .node-icon-wrapper {
    background: rgba(16, 185, 129, 0.2);
    color: #10b981;
}

.draggable-node.update-node .node-icon-wrapper {
    background: rgba(245, 158, 11, 0.2);
    color: #f59e0b;
}

.draggable-node.delete-node .node-icon-wrapper {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
}

.draggable-node.before_create-node .node-icon-wrapper {
    background: rgba(6, 182, 212, 0.2);
    color: #06b6d4;
}

.draggable-node.before_update-node .node-icon-wrapper {
    background: rgba(139, 92, 246, 0.2);
    color: #8b5cf6;
}

.draggable-node.before_delete-node .node-icon-wrapper {
    background: rgba(249, 115, 22, 0.2);
    color: #f97316;
}

.draggable-node.email-node .node-icon-wrapper {
    background: rgba(59, 130, 246, 0.2);
    color: #3b82f6;
}

.draggable-node.http_request-node .node-icon-wrapper {
    background: rgba(16, 185, 129, 0.2);
    color: #10b981;
}

.draggable-node.platform_notification-node .node-icon-wrapper {
    background: rgba(245, 158, 11, 0.2);
    color: #f59e0b;
}

/* No results */
.no-results {
    padding: 2.5rem 1.5rem;
    text-align: center;
}

.no-results-icon {
    font-size: 2rem;
    color: var(--text-secondary);
    opacity: 0.4;
    margin-bottom: 1rem;
    display: block;
}

.no-results-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
}

.no-results-sub {
    font-size: 0.78rem;
    color: var(--text-secondary);
    margin-bottom: 1rem;
}

/* Canvas remains accessible for drag and drop */

/* Drop Zone */
.workflow-canvas.drag-over {
    position: relative;
    background: rgba(79, 209, 199, 0.05);
}

.drop-zone-indicator {
    position: absolute;
    top: 20px;
    left: 20px;
    right: 20px;
    bottom: 20px;
    background: rgba(79, 209, 199, 0.08);
    border: 2px dashed #4fd1c7;
    border-radius: 12px;
    z-index: 999;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
}

.drop-zone-content {
    text-align: center;
    color: #4fd1c7;
    pointer-events: none;
}

.drop-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.8;
}

.drop-zone-content h5 {
    color: #4fd1c7;
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.drop-zone-content p {
    color: #95a5a6;
    margin: 0;
}

/* Bottom Drawer Styles */
.node-drawer {
    position: fixed;
    left: 50px;
    right: 0;
    bottom: 40px;
    height: auto;
    max-height: 80vh;
    background: var(--form-bg);
    border-top: 1px solid var(--border-default);
    z-index: 10200;
    transition: all 0.3s ease;
    box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.4);
    display: flex;
    flex-direction: column;
    transform: translateY(calc(100% + 40px));
}

.node-drawer.open {
    transform: translateY(0);
}

.drawer-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.drawer-header {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--border-default);
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--bg-card);
    flex-shrink: 0;
}

.drawer-header h5 {
    color: var(--text-primary);
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
}

.drawer-header .close-btn {
    color: var(--border-hover) !important;
    padding: 0.25rem !important;
}

.drawer-header .close-btn:hover {
    color: var(--text-primary) !important;
}

.drawer-body {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.3s ease 0.1s;
}

/* v-select styling for visibility */
.drawer-body .v-select,
.drawer-body >>> .v-select,
.popover-body >>> .v-select {
    background: var(--form-bg);
}

.drawer-body >>> .vs__dropdown-toggle,
.popover-body >>> .vs__dropdown-toggle {
    background: var(--form-bg);
    border: 1px solid var(--border-default);
    border-radius: 4px;
    padding: 0.375rem 0.75rem;
}

.drawer-body >>> .vs__dropdown-toggle:hover,
.popover-body >>> .vs__dropdown-toggle:hover {
    border-color: var(--border-hover);
}

.drawer-body >>> .vs__search,
.drawer-body >>> .vs__search:focus,
.popover-body >>> .vs__search,
.popover-body >>> .vs__search:focus {
    background: transparent;
    color: var(--text-primary);
}

.drawer-body >>> .vs__selected,
.popover-body >>> .vs__selected {
    color: var(--text-primary);
}

.drawer-body >>> .vs__dropdown-menu,
.popover-body >>> .vs__dropdown-menu {
    background: var(--form-bg);
    border: 1px solid var(--border-default);
}

.drawer-body >>> .vs__dropdown-option,
.popover-body >>> .vs__dropdown-option {
    color: var(--text-primary);
}

.drawer-body >>> .vs__dropdown-option--highlight,
.popover-body >>> .vs__dropdown-option--highlight {
    background: var(--border-hover);
}

/* Node Edit Popover on Canvas */
.node-edit-popover {
    position: absolute;
    background: var(--bg-card);
    border: 2px solid var(--border-default);
    border-radius: 8px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    min-width: 400px;
    min-height: 250px;
    z-index: 1000;
    animation: popoverFadeIn 0.2s ease;
    display: flex;
    flex-direction: column;
}

@keyframes popoverFadeIn {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

.node-edit-popover .popover-header {
    background: var(--form-bg);
    border-bottom: 1px solid var(--border-default);
    padding: 0.75rem 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px 6px 0 0;
    flex-shrink: 0;
}

.node-edit-popover .popover-header h6 {
    margin: 0;
    color: var(--text-primary);
    font-size: 0.95rem;
    font-weight: 600;
}

.node-edit-popover .popover-header .close-btn {
    color: var(--border-hover) !important;
    padding: 0 !important;
}

.node-edit-popover .popover-header .close-btn:hover {
    color: var(--text-primary) !important;
}

.node-edit-popover .popover-body {
    padding: 1rem;
    color: var(--text-primary);
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;
    min-height: 0;
}

.node-edit-popover .popover-footer {
    background: var(--form-bg);
    border-top: 1px solid var(--border-default);
    padding: 0.75rem 1rem;
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    border-radius: 0 0 6px 6px;
    flex-shrink: 0;
}

.popover-resize-handle {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 20px;
    height: 20px;
    cursor: nwse-resize;
    background: linear-gradient(
        135deg,
        transparent 0%,
        transparent 50%,
        var(--border-hover) 50%,
        var(--border-hover) 55%,
        transparent 55%,
        transparent 65%,
        var(--border-hover) 65%,
        var(--border-hover) 70%,
        transparent 70%
    );
    border-radius: 0 0 6px 0;
    z-index: 10;
}

.popover-resize-handle:hover {
    background: linear-gradient(
        135deg,
        transparent 0%,
        transparent 50%,
        var(--text-primary) 50%,
        var(--text-primary) 55%,
        transparent 55%,
        transparent 65%,
        var(--text-primary) 65%,
        var(--text-primary) 70%,
        transparent 70%
    );
}



.node-drawer.open .drawer-body {
    opacity: 1;
    transform: translateY(0);
}

.drawer-footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid var(--border-default);
    background: var(--bg-card);
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    flex-shrink: 0;
}

.drawer-footer .btn {
    min-width: 80px;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
    .workflow-sidebar-right {
        width: 300px;
        right: -300px;
    }
    
    .node-drawer {
        left: 50px;
        max-height: 85vh;
    }
}

@media (max-width: 768px) {
    .workflow-sidebar-right {
        width: 100%;
        right: -100%;
        top: 55px;
        bottom: 50px;
    }
    
    .workflow-sidebar-right.open {
        right: 0;
    }
    
    .node-drawer {
        left: 0;
        max-height: 90vh;
    }
    
    .drawer-body {
        padding: 1rem;
    }
    
    .drawer-footer {
        padding: 1rem;
    }
}

/* Event outputs / available variables panel */
.event-outputs-section {
    border: 1px solid rgba(99, 102, 241, 0.25);
    border-radius: 0.4rem;
    overflow: hidden;
}

.event-outputs-header {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 0.6rem;
    background: rgba(99, 102, 241, 0.08);
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: 600;
    color: #818cf8;
    user-select: none;
}

.event-outputs-header:hover {
    background: rgba(99, 102, 241, 0.14);
}

.event-outputs-chevron {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
}

.event-outputs-count {
    margin-left: auto;
    background: rgba(99, 102, 241, 0.2);
    color: #818cf8;
    border-radius: 0.75rem;
    padding: 0 0.4rem;
    font-size: 0.65rem;
    line-height: 1.4rem;
}

.event-outputs-list {
    padding: 0.35rem 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.event-output-item {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.35rem 0.5rem;
    border-radius: 0.3rem;
    background: rgba(15, 15, 25, 0.3);
}

.event-output-header {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}

.event-output-name {
    font-family: monospace;
    font-size: 0.72rem;
    color: #a5b4fc;
    font-weight: 600;
}

.event-output-type {
    font-size: 0.65rem;
    color: #34d399;
    background: rgba(52, 211, 153, 0.1);
    border-radius: 0.2rem;
    padding: 0 0.3rem;
    flex-shrink: 0;
}

.event-output-desc {
    font-size: 0.65rem;
    color: #6b7280;
}

.event-output-example {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.event-output-example-label {
    font-size: 0.6rem;
    color: #4b5563;
    font-style: italic;
    flex-shrink: 0;
}

.event-output-example-value {
    font-family: monospace;
    font-size: 0.65rem;
    color: #f59e0b;
    background: rgba(245, 158, 11, 0.08);
    border-radius: 0.2rem;
    padding: 0 0.3rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
}
</style>

<script>
import Input from '@/components/form/Input'
import Select from "@/components/form/Select"
import CodeEditor from '@/components/form/Editor/Code'
import _ from 'lodash'
import { Switch as cSwitch } from '@coreui/vue'
import Drawer from "vue-simple-drawer"

import Email from './Events/Email'
import PlatformNotifications from './Events/PlatformNotifications'
import PushNotification from './Events/PushNotification'
import HttpRequest from './Events/HttpRequest'
import Expression from './Events/Expression'
import ConditionExpression from './Events/ConditionExpression'
import ComponentExpression from './Events/ComponentExpression'
import SwitchExpression from './Events/SwitchExpression'
import IntegrationNode from './Events/IntegrationNode'
import Spark from './Events/Spark'
import CompletionNodeConfig from '@/views/integrations/protokol/kortex/CompletionNodeConfig'
import OCRNodeConfig from '@/views/integrations/protokol/kortex/OCRNodeConfig'
import EventSettingsNode from './Events/EventSettingsNode'
import EmailReceivedSettings from '@/views/integrations/protokol/inbox/EmailReceivedSettings'
import FlowChart from '../../flowchart-vue/src/components/flowchart';
import enhancedRender from '../../flowchart-vue/src/components/flowchart/enhanced-render';
import Helptip from '../../elements/Helptip'
import VSelect from 'vue-select'
import CreateModel from '@/components/base/Create'
import WorkflowHistory from '@/lib/WorkflowHistory'
import JsonTreeView from '@/components/elements/JsonTreeView'
import satelliteIcon from '@/assets/satellite.svg'

export default {
    components: {
        InputField: Input,
        SelectField: Select,
        Email,
        PlatformNotifications,
        PushNotification,
        HttpRequest,
        Expression,
        ConditionExpression,
        ComponentExpression,
        SwitchExpression,
        IntegrationNode,
        Spark,
        CompletionNodeConfig,
        OCRNodeConfig,
        EventSettingsNode,
        EmailReceivedSettings,
        FlowChart,
        CodeEditor,
        Helptip,
        VSelect,
        cSwitch,
        CreateModel,
        Drawer,
        Spark,
        JsonTreeView
    },
    props: {
       value: {
         type: Array
       },
       schema: {
         type: String,
         default: ''
       },
       fields: {
         type: Array,
         default: () => []
       },
       eventNames: {
          type: Array,
          default: function() {
            return [
                {
                    name: 'Before::Create',
                    label: {
                        locales: {
                            en_US: 'Before create',
                            sr: 'Pre kreiranja'
                        }
                    }
                },
                {
                    name: 'Create',
                    label: {
                        locales: {
                            en_US: 'Create',
                            sr: 'Kreirano'
                        }
                    }
                },
                {
                    name: 'Before::Update',
                    label: {
                        locales: {
                            en_US: 'Before update',
                            sr: 'Pre ažuriranja'
                        }
                    }
                },
                {
                    name: 'Update',
                    label: {
                        locales: {
                            en_US: 'Update',
                            sr: 'Ažurirano'
                        }
                    }
                },
                {
                    name: 'Before::Delete',
                    label: {
                        locales: {
                            en_US: 'Before delete',
                            sr: 'Pre brisanja'
                        }
                    }
                },
                {
                    name: 'Delete',
                    label: {
                        locales: {
                            en_US: 'Delete',
                            sr: 'Obrisano'
                        }
                    }
                },
                {
                    name: 'Recycle',
                    label: {
                        locales: {
                            en_US: 'Recycle',
                            sr: 'Recikliraj'
                        }
                    }
                }
            ];
          }
       },
       uuid: {
          type: String
       },

       disableDryRun: {
          type: Boolean,
          default: false
       },
       readonly: {
          type: Boolean,
          default: false
       }
    },
    data: function() {
        return {
            version: 1,
            nodes: [],
            connections: [],
            nodeModal: {
                show: false
            },
            isFullscreen: false,
            newNode: {
                modal: false,
                data: {
                    id: "",
                    name: "",
                    schema: "",
                    x: 0,
                    y: 0,
                    type: "",
                    action: "",
                    component: "",
                    paused: false,
                    parallel: true,
                    data: {},
                    connections: []
                }
            },
            editNode: {
                modal: false,
                data: {}
            },
            actions: [
                {
                    value: "email",
                    text: this.$t('sendEmail')
                },
                // { we need to support this option better
                //     value: "push_notification",
                //     text: this.$t('pushNotification')
                // },
                {
                    value: "http_request",
                    text: this.$t('httpRequest')
                },
                // {
                //     value: "component",
                //     text: this.$t('component')
                // },
                {
                    value: "expression",
                    text: this.$t('expression')
                },
                {
                    value: "platform_notification",
                    text: this.$t('platformNotification')
                }
            ],
            errors: [],
            requestData: [],
            enhancedRender: enhancedRender,
            timers: [
                {
                    value: "5min",
                    text: this.$t('every5')
                },
                {
                    value: "30min",
                    text: this.$t('every30')
                },
                {
                    value: "1h",
                    text: this.$t('everyHour')
                },
                {
                    value: "3h",
                    text: this.$t('every3Hour')
                },
                {
                    value: "6h",
                    text: this.$t('every6Hour')
                },
                {
                    value: "9h",
                    text: this.$t('every9Hour')
                },
                {
                    value: "12h",
                    text: this.$t('every12Hour')
                },
                {
                    value: "1d",
                    text: this.$t('everyDay')
                },
                {
                    value: "mondays",
                    text: this.$t('everyMonday')
                },
                {
                    value: "tuesdays",
                    text: this.$t('everyTuesday')
                },
                {
                    value: "wednesdays",
                    text: this.$t('everyWednesday')
                },
                {
                    value: "thursdays",
                    text: this.$t('everyThursday')
                },
                {
                    value: "fridays",
                    text: this.$t('everyFriday')
                },
                {
                    value: "saturdays",
                    text: this.$t('everySaturday')
                },
                {
                    value: "sundays",
                    text: this.$t('everySunday')
                },
                {
                    value: "month_start",
                    text: this.$t('everyMonthStart')
                },
                {
                    value: "month_end",
                    text: this.$t('everyMonthEnd')
                }
            ],
            recycleExprErrors: [],
            controlsContent: 'Double-click canvas to open node library, or drag & drop from sidebar <br />',
            saving: false,

            editConnection: {
                name: ''
            },
            editConnectionModal: false,
            showDryRunResults: true,
            dryRunResults: [],
            resultsSidebar: false,
            inspectResult: null,
            dryRunInput: null,
            dryRunInputModal: false,
            dryRunInputErrors: [],
            dryRunEvent: null,
            dryRunMockedModel: {},
            playNodeModal: false,
            playNodeTarget: null,
            playNodeInput: '{}',
            playNodeInputErrors: [],
            playNodeResults: [],
            playNodeRunning: false,
            playNodeExecutionTime: null,
            playNodeExecutionUUID: null,
            playNodePollTimer: null,
            playNodeStatus: null,
            playPopoverX: 0,
            playPopoverY: 0,
            playPopoverWidth: 700,
            playPopoverHeight: 550,
            isPlayDragging: false,
            playDragStartX: 0,
            playDragStartY: 0,
            isPlayResizing: false,
            playResizeStartX: 0,
            playResizeStartY: 0,
            playNodeDetail: null,
            detailPopoverX: 0,
            detailPopoverY: 0,
            detailPopoverWidth: 700,
            detailPopoverHeight: 550,
            isDetailDragging: false,
            detailDragStartX: 0,
            detailDragStartY: 0,
            isDetailResizing: false,
            detailResizeStartX: 0,
            detailResizeStartY: 0,
            detailSearchQuery: '',
            selectedNode: null,
            globalEnvVars: null,
            // Right Sidebar and Drag & Drop
            sidebarOpen: false,
            isDragOver: false,
            draggedNodeType: null,
            // Event outputs panel
            showEventOutputs: true,
            // Map of customView strings to Vue components for integration workflow node configuration
            customViewComponents: {
                'kortex/CompletionNodeConfig': CompletionNodeConfig,
                'kortex/OCRNodeConfig': OCRNodeConfig
            },
            // Map of customSettingsView strings to Vue components for event settings
            customSettingsViewComponents: {
                'inbox/EmailReceivedSettings': EmailReceivedSettings
            },
            // Node Filters
            nodeSearchFilter: '',
            activeCategoryFilter: 'all',
            satelliteWorkflowNodes: [],
            // Local History System
            history: [],
            historyIndex: -1,
            maxHistorySize: 50,
            historyModal: false,
            // Popover dragging
            isDragging: false,
            dragStartX: 0,
            dragStartY: 0,
            popoverX: 0,
            popoverY: 0,
            // Popover resizing
            isResizing: false,
            popoverWidth: 700,
            popoverHeight: 550
        }
    },
    computed: {
        isSparkActive() {
            return this.$store.getters.isIntegrationActive('protokol-spark')
        },
        content: {
            get() {
                return this.value
            },

            set(val) {
                this.$emit('input', val)
            }
        },

        // Available schemas from component fields
        availableSchemas() {
            if (!this.fields || !Array.isArray(this.fields)) {
                return [];
            }
            
            // Extract unique schemas from fields that have a schema property
            const schemas = this.fields
                .filter(field => field.schema)
                .map(field => field.schema)
                .filter((value, index, self) => self.indexOf(value) === index) // unique
                .sort();
            
            return schemas;
        },

        // Position popover near the node being edited or at dragged position
        dragPosition() {
            if (this.popoverX !== 0 || this.popoverY !== 0) {
                return {
                    position: 'fixed',
                    left: `${this.popoverX}px`,
                    top: `${this.popoverY}px`,
                    zIndex: 1000
                };
            }
            return {
                position: 'fixed',
                left: `calc(50vw - ${this.popoverWidth / 2}px)`,
                top: `calc(50vh - ${this.popoverHeight / 2}px)`,
                zIndex: 1000
            };
        },

        playDragPosition() {
            if (this.playPopoverX !== 0 || this.playPopoverY !== 0) {
                return {
                    position: 'fixed',
                    left: `${this.playPopoverX}px`,
                    top: `${this.playPopoverY}px`,
                    zIndex: 1050
                };
            }
            return {
                position: 'fixed',
                left: `calc(50vw - ${this.playPopoverWidth / 2}px)`,
                top: `calc(50vh - ${this.playPopoverHeight / 2}px)`,
                zIndex: 1050
            };
        },

        detailDragPosition() {
            if (this.detailPopoverX !== 0 || this.detailPopoverY !== 0) {
                return {
                    position: 'fixed',
                    left: `${this.detailPopoverX}px`,
                    top: `${this.detailPopoverY}px`,
                    zIndex: 1060
                };
            }
            return {
                position: 'fixed',
                left: `calc(50vw - ${this.detailPopoverWidth / 2}px)`,
                top: `calc(50vh - ${this.detailPopoverHeight / 2}px)`,
                zIndex: 1060
            };
        },

        nodeDetailState() {
            if (!this.playNodeDetail) return {};
            return this.playNodeDetail.state || this.playNodeDetail.State || {};
        },

        // Event nodes
        triggerNodes() {
            // Map eventNames prop to system events format
            const systemEvents = (this.eventNames || []).map(event => {
                const eventAction = event.name.toLowerCase().replace(/\./g, '_').replace(/::/g, '_');
                
                // Handle both object labels and string labels (in case of Vue reactivity mutation)
                let translatedLabel;
                if (typeof event.label === 'object' && event.label !== null && event.label.locales) {
                    // Label is an object with locales - translate it
                    translatedLabel = this.$translateLabel(event.label, event.name);
                } else if (typeof event.label === 'string') {
                    // Label was already converted to string (by Vue reactivity or previous translation)
                    translatedLabel = event.label;
                } else {
                    // Fallback to event name
                    translatedLabel = event.name;
                }
                
                return {
                    type: 'event',
                    action: eventAction,
                    eventName: event.name,
                    name: event.name,
                    label: translatedLabel,
                    icon: this.getEventIcon(event.name),
                    color: this.getEventColor(event.name),
                    description: `Triggers on ${translatedLabel}`,
                    _integration: event._integration || null,
                    outputs: event.outputs || [],
                    settings: (event._integration && event._integration.settings) ? event._integration.settings : [],
                    customSettingsView: (event._integration && event._integration.customSettingsView) ? event._integration.customSettingsView : null
                };
            });
            
            // Add custom event as the first option
            const customEvent = {
                type: 'event',
                action: 'custom',
                eventName: 'custom',
                label: this.$t('customEvent'),
                icon: '⚡',
                color: '#4fd1c7',
                description: this.$t('customEventTrigger')
            };

            // Return custom event first, then system events
            return [customEvent, ...systemEvents];
        },

        // Operation nodes
        actionNodes() {
            const builtIn = [
                {
                    type: 'operation',
                    action: 'expression',
                    label: this.$t('expression'),
                    icon: '{}',
                    color: '#8b5cf6', 
                    description: this.$t('customJavascriptExpression')
                }
            ];

            // Collect workflownodes from active integrations
            const integrations = this.$store.getters.getIntegrations || [];
            const integrationNodes = [];

            for (const integration of integrations) {
                if (!integration.template || !integration.template.workflownodes) continue;
                
                for (const node of integration.template.workflownodes) {
                    integrationNodes.push({
                        type: 'operation',
                        action: 'integration',
                        label: this.$translateLabel(node.label, node.name),
                        description: this.$translateLabel(node.description, node.name),
                        icon: node.icon || integration.template.icon || '🔌',
                        color: '#0ea5e9',  // sky-500 for integration nodes
                        // Metadata for node creation
                        _integration: {
                            node_name: node.name,
                            integration_id: integration.template.id,
                            integration_label: integration.template.label,
                            integration_icon: node.icon ? (integration.template.icon || null) : null,
                            node_label: node.label,
                            node_description: node.description,
                            node_icon: node.icon,
                            inputs: node.inputs,
                            outputs: node.outputs,
                            customView: node.customView || null
                        }
                    });
                }
            }

            return [...builtIn, ...integrationNodes, ...this.satelliteWorkflowNodes];
        },

        // Logic nodes
        logicNodes() {
            return [
                {
                    type: 'condition',
                    action: 'condition',
                    label: this.$t('condition'),
                    icon: '?',
                    color: '#f59e0b',
                    description: this.$t('makesDecision')
                },
                {
                    type: 'switch',
                    action: 'switch',
                    label: this.$t('switch'),
                    icon: '⚡',
                    color: '#3b82f6',
                    description: this.$t('multiplePaths')
                },
                {
                    type: 'each',
                    action: 'each',
                    label: this.$t('each'),
                    icon: '[...]',
                    color: '#10b981',
                    description: this.$t('repeatsItems')
                }
            ]
        },

        // Legacy compatibility 
        nodeTypes() {
            return [
                ...this.triggerNodes,
                ...this.actionNodes,
                ...this.logicNodes
            ]
        },

        rulesetLibs() {
            return [
                {
                    name: 'Lodash',
                    varName: "_",
                    docs: "https://lodash.com/"
                },
                {
                    name: 'moment',
                    varName: "moment",
                    docs: "https://momentjs.com/"
                }
            ]
        },

        newNodeName: {
            set(val) {
                this.newNode.data.name = val.replaceAll(" ","_");
            },

            get() {
                return this.newNode.data.name ? this.newNode.data.name.replaceAll(" ", "_") : '';
            }
        },

        editConnectionName: {
            set(val) {
                this.editConnection.name = val.replaceAll(" ","_");
            },

            get() {
                return this.editConnection.name
            }
        },

        editNodeName: {
            set(val) {
                const cleanedVal = val.replaceAll(" ","_");
                this.editNode.data.name = cleanedVal;
                // Also update nested data.name
                if (this.editNode.data.data) {
                    this.editNode.data.data.name = cleanedVal;
                }
            },

            get() {
                return this.editNode.data.name ? this.editNode.data.name.replaceAll(" ", "_") : '';
            }
        },

        isDryRunCustomEvent() {
            return this.eventNamesFormated.indexOf(this.dryRunEvent) === -1
        },

        eventNamesFormated() {
            console.log('Formatting event names for display', this.eventNames);
            return this.eventNames.map(e => {
                e.label = this.$translateLabel(e.label, e.name)

                return e
            })
        },

        // Filtered node arrays for the sidebar
        filteredTriggerNodes() {
            return this.filterNodesByCategory(this.triggerNodes, 'events');
        },

        filteredActionNodes() {
            return this.filterNodesByCategory(this.actionNodes, 'operations');  
        },

        filteredLogicNodes() {
            return this.filterNodesByCategory(this.logicNodes, 'logic');
        },

        hasNoFilteredResults() {
            return (
                this.filteredTriggerNodes.length === 0 &&
                this.filteredActionNodes.length === 0 &&
                this.filteredLogicNodes.length === 0
            );
        },

        // History system computed properties
        historyStorageKey() {
            // Use UUID if available, otherwise create a fallback key based on component context
            if (this.uuid) {
                return `workflow-history-${this.uuid}`;
            }
            // Fallback: use a hash of the initial content or a generic key
            const contentHash = this.content ? JSON.stringify(this.content).slice(0, 50) : 'default';
            return `workflow-history-fallback-${btoa(contentHash).slice(0, 10)}`;
        },

        canUndo() {
            return this.historyIndex > 0;
        },

        canRedo() {
            return this.historyIndex < this.history.length - 1;
        },

        currentHistoryEntry() {
            if (this.historyIndex >= 0 && this.historyIndex < this.history.length) {
                return this.history[this.historyIndex];
            }
            return null;
        }
    },
    watch: {
        // Watch for changes in nodes array to keep FlowChart in sync
        nodes: {
            handler(newNodes) {
                console.log('Nodes array changed, length:', newNodes.length);
                // Update content to keep it in sync
                this.content = JSON.parse(JSON.stringify(newNodes));
            },
            deep: true
        },
        
        // Watch for changes in connections array
        connections: {
            handler(newConnections) {
                console.log('Connections array changed, length:', newConnections.length);
            },
            deep: true
        },

        "editNode.data": {
            handler: _.debounce(function() {
                //this.$refs.chart.save()
            }, 500),

            deep: true
        }
    },
    methods: {
        modelChanged(m) {
            this.dryRunMockedModel = m 
        },

        // Parse icon to determine if it's a bootstrap icon or emoji
        parseIcon(icon) {
            if (!icon) return { type: 'emoji', value: '⚙' };
            if (typeof icon === 'string' && icon.startsWith('b-icon:')) {
                return { type: 'b-icon', value: icon.substring(7) };
            }
            if (typeof icon === 'string' && icon.startsWith('image:')) {
                return { type: 'image', value: icon.substring(6) };
            }
            if (typeof icon === 'string' && icon.startsWith('img:')) {
                return { type: 'image', value: icon.substring(4) };
            }
            return { type: 'emoji', value: icon };
        },

        toggleResultsSidebar() {
            this.resultsSidebar = !this.resultsSidebar
        },

        // History System Methods
        syncWithFlowChart() {
            // Sync component state with FlowChart internal state
            if (this.$refs.chart && typeof this.$refs.chart.getState === 'function') {
                const chartState = this.$refs.chart.getState();
                if (chartState) {
                    console.log('Syncing with FlowChart state');
                    this.nodes = JSON.parse(JSON.stringify(chartState.nodes || this.nodes));
                    this.connections = JSON.parse(JSON.stringify(chartState.connections || this.connections));
                    this.content = JSON.parse(JSON.stringify(this.nodes));
                    return true;
                }
            }
            return false;
        },

        forceChartSync() {
            // Force the FlowChart to save its current state to sync with component
            console.log('Forcing chart sync before critical operation');
            if (this.$refs.chart && typeof this.$refs.chart.save === 'function') {
                // Temporarily disable history saving during sync
                const tempFlag = this._shouldSaveToHistory;
                this._shouldSaveToHistory = null;
                this.$refs.chart.save();
                this._shouldSaveToHistory = tempFlag;
                return true;
            }
            return false;
        },

        hasWorkflowChanged() {
            if (this.history.length === 0) {
                console.log('No history, changes detected');
                return true;
            }
            
            // Try to get the most up-to-date state from FlowChart if available
            let currentNodes = this.nodes;
            let currentConnections = this.connections;
            
            // If FlowChart component is available and has a getState method, use that
            if (this.$refs.chart && typeof this.$refs.chart.getState === 'function') {
                const chartState = this.$refs.chart.getState();
                if (chartState) {
                    currentNodes = chartState.nodes || this.nodes;
                    currentConnections = chartState.connections || this.connections;
                }
            }
            
            const currentState = {
                nodes: JSON.stringify(currentNodes),
                connections: JSON.stringify(currentConnections)
            };
            
            const lastHistoryEntry = this.history[this.historyIndex];
            if (!lastHistoryEntry) {
                console.log('No last history entry, changes detected');
                return true;
            }
            
            const lastState = {
                nodes: JSON.stringify(lastHistoryEntry.nodes),
                connections: JSON.stringify(lastHistoryEntry.connections)
            };
            
            const nodesChanged = currentState.nodes !== lastState.nodes;
            const connectionsChanged = currentState.connections !== lastState.connections;
            
            console.log('Change detection:', {
                nodesChanged,
                connectionsChanged,
                currentNodeCount: currentNodes.length,
                lastNodeCount: lastHistoryEntry.nodes.length,
                currentConnectionCount: currentConnections.length,
                lastConnectionCount: lastHistoryEntry.connections.length,
                historyIndex: this.historyIndex,
                historyLength: this.history.length,
                usingChartState: !!(this.$refs.chart && typeof this.$refs.chart.getState === 'function')
            });
            
            return nodesChanged || connectionsChanged;
        },

        saveToHistory(action = 'Manual Save') {
            console.log('saveToHistory called:', action, 'UUID:', this.uuid, 'nodes:', this.nodes.length);
            
            // Sync with FlowChart state before saving to ensure we have the latest data
            this.syncWithFlowChart();
            
            // Force save for certain critical actions
            const forceSaveActions = ['Connection Created', 'Added', 'Removed', 'Edited'];
            const shouldForceSave = forceSaveActions.some(forceAction => action.includes(forceAction));
            
            // Only save if there are actual changes (unless it's a forced action)
            if (!shouldForceSave && !this.hasWorkflowChanged()) {
                console.log('No workflow changes detected, skipping history save');
                return;
            }
            
            if (shouldForceSave) {
                console.log('Force saving due to critical action:', action);
            }
            
            // Try to get the most up-to-date state from FlowChart if available
            let nodesToSave = this.nodes;
            let connectionsToSave = this.connections;
            
            // If FlowChart component is available and has a getState method, use that for more accurate state
            if (this.$refs.chart && typeof this.$refs.chart.getState === 'function') {
                const chartState = this.$refs.chart.getState();
                if (chartState) {
                    nodesToSave = chartState.nodes || this.nodes;
                    connectionsToSave = chartState.connections || this.connections;
                    console.log('Using FlowChart state for history save');
                }
            }
            
            const timestamp = new Date().toISOString();
            const snapshot = {
                id: Date.now(),
                timestamp,
                action,
                nodes: JSON.parse(JSON.stringify(nodesToSave)),
                connections: JSON.parse(JSON.stringify(connectionsToSave)),
                nodeCount: nodesToSave.length,
                connectionCount: connectionsToSave.length
            };

            // Remove any history entries after current index (when we're not at the end)
            if (this.historyIndex < this.history.length - 1) {
                this.history = this.history.slice(0, this.historyIndex + 1);
            }

            // Add new entry
            this.history.push(snapshot);
            this.historyIndex = this.history.length - 1;
            
            console.log('History entry added:', action, 'Total entries:', this.history.length, 'Current index:', this.historyIndex);

            // Limit history size
            if (this.history.length > this.maxHistorySize) {
                this.history = this.history.slice(-this.maxHistorySize);
                this.historyIndex = this.history.length - 1;
            }

            // Save to localStorage
            this.saveHistoryToStorage();
        },

        undo() {
            if (this.canUndo) {
                this.historyIndex--;
                this.restoreFromHistory();
            }
        },

        redo() {
            if (this.canRedo) {
                this.historyIndex++;
                this.restoreFromHistory();
            }
        },

        restoreFromHistory() {
            const entry = this.currentHistoryEntry;
            if (entry) {
                console.log('Restoring from history:', entry.action, 'nodes:', entry.nodes.length, 'connections:', entry.connections.length);
                
                // Deep clone the history data to avoid reference issues
                this.nodes = JSON.parse(JSON.stringify(entry.nodes));
                this.connections = JSON.parse(JSON.stringify(entry.connections));
                
                // Enrich nodes with current translations
                this.enrichNodesWithTranslations(this.nodes);
                
                // Update content first
                this.content = JSON.parse(JSON.stringify(this.nodes));
                
                // Force FlowChart re-render by incrementing version
                this.version++;
                
                // Wait for the next Vue tick to ensure the FlowChart component has been re-rendered
                this.$nextTick(() => {
                    // Try multiple approaches to sync the FlowChart state
                    if (this.$refs.chart) {
                        // Method 1: If FlowChart has updateState method
                        if (typeof this.$refs.chart.updateState === 'function') {
                            this.$refs.chart.updateState(this.nodes, this.connections);
                        }
                        
                        // Method 2: If FlowChart has setState method
                        else if (typeof this.$refs.chart.setState === 'function') {
                            this.$refs.chart.setState({
                                nodes: this.nodes,
                                connections: this.connections
                            });
                        }
                        
                        // Method 3: Force a complete refresh by calling save
                        else if (typeof this.$refs.chart.refresh === 'function') {
                            this.$refs.chart.refresh();
                        }
                        
                        // Method 4: Fallback - trigger a save to sync internal state
                        else {
                            setTimeout(() => {
                                if (this.$refs.chart && typeof this.$refs.chart.save === 'function') {
                                    this.$refs.chart.save();
                                }
                            }, 100);
                        }
                    }
                    
                    // Emit save event to notify parent components
                    this.$emit('save', this.content);
                    
                    console.log('History restoration complete');
                });
            }
        },

        saveHistoryToStorage() {
            try {
                const historyData = {
                    uuid: this.uuid || 'fallback',
                    history: this.history,
                    historyIndex: this.historyIndex
                };
                const storageKey = this.historyStorageKey;
                console.log('Saving history to localStorage with key:', storageKey, 'entries:', this.history.length, 'UUID:', this.uuid);
                localStorage.setItem(storageKey, JSON.stringify(historyData));
            } catch (error) {
                console.warn('Failed to save history to localStorage:', error);
            }
        },

        loadHistoryFromStorage() {
            try {
                const storageKey = this.historyStorageKey;
                const stored = localStorage.getItem(storageKey);
                console.log('Loading history from localStorage with key:', storageKey, 'found:', !!stored, 'UUID:', this.uuid);
                
                if (stored) {
                    const historyData = JSON.parse(stored);
                    this.history = historyData.history || [];
                    this.historyIndex = historyData.historyIndex || -1;
                    console.log('Loaded history entries:', this.history.length, 'current index:', this.historyIndex);
                } else {
                    this.history = [];
                    this.historyIndex = -1;
                    console.log('No existing history found, starting fresh');
                }
            } catch (error) {
                console.warn('Failed to load history from localStorage:', error);
                this.history = [];
                this.historyIndex = -1;
            }
        },

        clearHistory() {
            this.history = [];
            this.historyIndex = -1;
            const storageKey = this.historyStorageKey;
            console.log('Clearing history from localStorage with key:', storageKey);
            localStorage.removeItem(storageKey);
        },

        openHistoryModal() {
            this.historyModal = true;
        },

        closeHistoryModal() {
            this.historyModal = false;
        },

        restoreToHistoryEntry(entry) {
            // Find the index of this entry
            const index = this.history.findIndex(h => h.id === entry.id);
            if (index !== -1) {
                this.historyIndex = index;
                this.restoreFromHistory();
                this.closeHistoryModal();
            }
        },

        formatHistoryTimestamp(timestamp) {
            const date = new Date(timestamp);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays < 7) return `${diffDays}d ago`;
            
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        },

        // New UI/UX methods
        createNodeOfType(nodeType) {
            // Create node at center of canvas
            const centerX = 300;
            const centerY = 200;
            
            this.newNode.data.type = nodeType;
            this.newNode.data.x = centerX;
            this.newNode.data.y = centerY;
            this.newNode.modal = true;
        },

        // Node filtering methods
        filterNodesByCategory(nodes, category) {
            if (!nodes) return [];
            
            // Filter by search term first
            let filtered = nodes.filter(node => {
                if (!this.nodeSearchFilter) return true;
                const searchTerm = this.nodeSearchFilter.toLowerCase();
                return (
                    (node.label && node.label.toLowerCase().includes(searchTerm)) ||
                    (node.description && node.description.toLowerCase().includes(searchTerm)) ||
                    (node.action && node.action.toLowerCase().includes(searchTerm)) ||
                    (node.eventName && node.eventName.toLowerCase().includes(searchTerm)) ||
                    (this.isIntegrationOperationNode(node) && this.getIntegrationLabel(node._integration).toLowerCase().includes(searchTerm)) ||
                    (node.type && node.type.toLowerCase().includes(searchTerm))
                );
            });

            // Then filter by category if not 'all'
            if (this.activeCategoryFilter !== 'all' && this.activeCategoryFilter !== category) {
                return [];
            }

            return filtered;
        },

        setCategoryFilter(category) {
            this.activeCategoryFilter = category;
        },

        shouldShowCategory(category) {
            // Always show if 'all' is selected or if the specific category is selected
            return this.activeCategoryFilter === 'all' || this.activeCategoryFilter === category;
        },

        clearFilters() {
            this.nodeSearchFilter = '';
            this.activeCategoryFilter = 'all';
        },

        isIntegrationOperationNode(nodeType) {
            return !!(nodeType && nodeType._integration);
        },

        getIntegrationLabel(integrationMeta) {
            if (!integrationMeta) {
                return '';
            }

            return this.$translateLabel(integrationMeta.integration_label, integrationMeta.integration_id || integrationMeta.satellite_id);
        },

        buildNodeTooltip(nodeType) {
            var tooltip = nodeType.description || '';
            if (nodeType.outputs && nodeType.outputs.length > 0) {
                var varNames = nodeType.outputs.map(function(o) { return o.name; }).join(', ');
                tooltip = tooltip ? tooltip + '\n\nOutputs: ' + varNames : 'Outputs: ' + varNames;
            }
            return tooltip;
        },

        async loadSatelliteWorkflowNodes() {
            if (!this.$integrationApi) {
                this.satelliteWorkflowNodes = [];
                return;
            }

            try {
                const env = localStorage.getItem('current_env') || (this.$store.getters.isLiveEnv ? 'live' : 'dev');
                const resp = await this.$integrationApi.get('/satellites/templates');
                const satellites = Array.isArray(resp.data) ? resp.data : [];
                const nodeGroups = await Promise.all(satellites.map((satellite) => this.loadSatelliteWorkflowNodesForTemplate(satellite, env)));
                this.satelliteWorkflowNodes = _.flatten(nodeGroups).filter(Boolean);
            } catch (err) {
                this.$debugError(err);
                this.satelliteWorkflowNodes = [];
            }
        },

        async loadSatelliteWorkflowNodesForTemplate(satellite, env) {
            if (!satellite || satellite.status === 'disabled') return [];

            const activeVersion = env === 'dev' ? satellite.dev_version : satellite.live_version;
            if (!activeVersion) return [];

            const resp = await this.$integrationApi.get(`/satellites/templates/${satellite.id}/versions`);
            const versions = Array.isArray(resp.data) ? resp.data : [];
            const schema = versions.find((item) => item.version === activeVersion);
            const nodes = schema && Array.isArray(schema.workflownodes) ? schema.workflownodes : [];

            return nodes.map((node) => ({
                type: 'operation',
                action: 'integration',
                label: this.translateSatelliteText(node.label, node.name),
                description: this.translateSatelliteText(node.description, node.name),
                icon: this.getSatelliteWorkflowNodeIcon(node, satellite),
                color: '#14b8a6',
                outputs: node.outputs || [],
                _integration: {
                    kind: 'satellite',
                    node_name: node.name,
                    integration_id: null,
                    satellite_id: satellite.id,
                    integration_label: this.normalizeSatelliteLocaleText(satellite.label, satellite.id),
                    integration_icon: node.icon ? this.getSatelliteTemplateIcon(satellite) : null,
                    node_label: this.normalizeSatelliteLocaleText(node.label, node.name),
                    node_description: this.normalizeSatelliteLocaleText(node.description, node.name),
                    node_icon: node.icon,
                    inputs: node.inputs || [],
                    outputs: node.outputs || [],
                    customView: null
                }
            }));
        },

        getSatelliteWorkflowNodeIcon(node, satellite) {
            if (node && node.icon) {
                return node.icon;
            }

            return this.getSatelliteTemplateIcon(satellite);
        },

        getSatelliteTemplateIcon(satellite) {
            const icon = satellite && satellite.icon ? satellite.icon : '';
            if (!icon || icon === 'satellite' || icon === 'b-icon:satellite') {
                return `image:${satelliteIcon}`;
            }

            return icon;
        },

        normalizeSatelliteLocaleText(value, fallback) {
            if (typeof value === 'string') {
                return { locales: { en_US: value } };
            }

            if (value && typeof value === 'object') {
                if (value.locales) {
                    return value;
                }

                return { locales: value };
            }

            return { locales: { en_US: fallback || '' } };
        },

        translateSatelliteText(value, fallback) {
            return this.$translateLabel(this.normalizeSatelliteLocaleText(value, fallback), fallback);
        },

        formatOutputExample(value) {
            if (value === null || value === undefined) {
                return '';
            }
            if (typeof value === 'object') {
                return JSON.stringify(value);
            }
            return String(value);
        },

        getFilteredNodeCount(category) {
            switch (category) {
                case 'trigger':
                    return this.filteredTriggerNodes.length;
                case 'action':
                    return this.filteredActionNodes.length;
                case 'logic':
                    return this.filteredLogicNodes.length;
                default:
                    return this.filteredTriggerNodes.length + this.filteredActionNodes.length + this.filteredLogicNodes.length;
            }
        },

        // Sidebar methods
        openNodeSidebar() {
            this.sidebarOpen = true;
        },

        closeSidebar() {
            this.sidebarOpen = false;
        },

        handleContainerClick(event) {
            // Close sidebar if clicking on the canvas area (not on nodes or controls)
            if (this.sidebarOpen && event.target.classList.contains('workflow-canvas')) {
                this.closeSidebar();
            }
        },

        // Drag & Drop methods
        onDragStart(event, nodeType) {
            this.draggedNodeType = nodeType;
            event.dataTransfer.setData('text/plain', JSON.stringify(nodeType));
            event.dataTransfer.effectAllowed = 'copy';
            
            // Create drag image
            const dragImage = event.target.cloneNode(true);
            dragImage.style.transform = 'rotate(5deg)';
            dragImage.style.opacity = '0.8';
            document.body.appendChild(dragImage);
            event.dataTransfer.setDragImage(dragImage, 50, 25);
            
            // Clean up drag image after a short delay
            setTimeout(() => {
                document.body.removeChild(dragImage);
            }, 0);
        },

        onDragEnd() {
            this.draggedNodeType = null;
        },

        onCanvasDragOver(event) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
        },

        onCanvasDragEnter(event) {
            event.preventDefault();
            this.isDragOver = true;
        },

        onCanvasDragLeave(event) {
            // Only hide drop indicator if we're actually leaving the canvas
            if (!event.currentTarget.contains(event.relatedTarget)) {
                this.isDragOver = false;
            }
        },

        onCanvasDrop(event) {
            event.preventDefault();
            this.isDragOver = false;
            
            if (!this.draggedNodeType) return;
            
            // Get drop coordinates relative to canvas
            const canvas = event.currentTarget;
            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            
            // Create new node at drop position
            this.createNodeAtPosition(this.draggedNodeType, x, y);
            this.draggedNodeType = null;
            
            // Keep sidebar open for easy multiple node additions
            // User can close it manually or click elsewhere
        },

        createNodeAtPosition(nodeType, x, y) {
            // Generate unique integer ID
            const nodeId = Date.now() + Math.floor(Math.random() * 1000);
            
            const nodeName = this.getNodeName(nodeType);
            // For custom events, don't use the translated label - use null so name is shown
            const nodeLabel = (nodeType.type === 'event' && nodeType.action === 'custom') ? null : nodeType.label;
            
            // Handle integration nodes specially
            let nodeData;
            if (nodeType.action === 'integration' && nodeType._integration) {
                // Integration node - populate with metadata and empty inputs
                const inputs = {};
                if (nodeType._integration.inputs) {
                    nodeType._integration.inputs.forEach(input => {
                        inputs[input.name] = input.default !== undefined ? input.default : null;
                    });
                }
                
                nodeData = {
                    node_name: nodeType._integration.node_name,
                    integration_id: nodeType._integration.integration_id,
                    satellite_id: nodeType._integration.satellite_id || '',
                    integration_label: nodeType._integration.integration_label,
                    node_label: nodeType._integration.node_label,
                    node_icon: nodeType._integration.node_icon,
                    inputs: inputs,
                    // Keep the full integration metadata for the configuration component
                    _integration: nodeType._integration
                };
            } else if (nodeType.action === 'recycle' || nodeType.name === 'Recycle') {
                // Recycle node
                nodeData = { 
                    timer: '1h', 
                    expression: 'true',
                    name: nodeName,
                    action: nodeType.action || this.getDefaultAction(nodeType.type),
                    type: nodeType.type,
                    label: nodeType.label
                };
            } else {
                // Other nodes
                nodeData = {
                    name: nodeName,
                    action: nodeType.action || this.getDefaultAction(nodeType.type),
                    type: nodeType.type,
                    label: nodeLabel
                };
            }
            
            // Set up new node data with generic names
            const newNodeData = {
                id: nodeId,
                name: nodeName,
                x: Math.max(0, x - 100), // Center the node
                y: Math.max(0, y - 40),
                type: nodeType.type,
                action: nodeType.action || this.getDefaultAction(nodeType.type),
                component: "",
                paused: false,
                parallel: true,
                data: nodeData,
                connections: [],
                icon: nodeType.icon, // Add the icon from the node type
                label: nodeLabel, // Use null for custom events so name is displayed
                eventName: nodeType.eventName || nodeType.name // Keep eventName for backward compatibility
            };
            
            // Add node directly to the nodes array
            this.nodes.push(newNodeData);
            
            // Trigger re-render
            this.version++;
            
            // Save to history when node added
            this.saveToHistory(`Added ${nodeType.label || nodeType.name} Node`);
            
            // Update content and emit save
            this.content = JSON.parse(JSON.stringify(this.nodes));
            this.$emit('save', this.content);
            
            // Note: Sidebar stays open for adding multiple nodes
            // User can close it manually or click on canvas
        },

        getNodeName(nodeType) {
            // For event nodes, use predefined eventName or allow custom for custom events
            if (nodeType.type === 'event') {
                if (nodeType.action === 'custom') {
                    // For custom events, use generic naming that user can customize
                    const nodeCount = this.nodes.filter(n => n.type === nodeType.type && n.action === nodeType.action).length + 1;
                    return `CustomEvent_${nodeCount}`;
                } else {
                    // For standard events, use name (for external events) or eventName (for system events)
                    return nodeType.name || nodeType.eventName || nodeType.label;
                }
            }
            
            // For operation nodes, use the action to determine the name
            if (nodeType.type === 'operation' && nodeType.action) {
                const nodeCount = this.nodes.filter(n => n.type === nodeType.type && n.action === nodeType.action).length + 1;
                switch (nodeType.action) {
                    case 'expression':
                        return `Expression_${nodeCount}`;
                    case 'integration':
                        // For integration nodes, use the node label
                        if (nodeType._integration && nodeType._integration.node_label) {
                            const integrationCount = this.nodes.filter(n => 
                                n.type === nodeType.type && 
                                n.action === nodeType.action && 
                                n.data && n.data.node_name === nodeType._integration.node_name
                            ).length + 1;
                            const labelText = this.$translateLabel(nodeType._integration.node_label, nodeType._integration.node_name);
                            return `${labelText.replace(/\s+/g, '_')}_${integrationCount}`;
                        }
                        return `Integration_${nodeCount}`;
                        
                    default:
                        return `${nodeType.action.charAt(0).toUpperCase() + nodeType.action.slice(1)}_${nodeCount}`;
                }
            }
            
            // For other node types, use the type
            const nodeCount = this.nodes.filter(n => n.type === nodeType.type).length + 1;
            switch (nodeType.type) {
                case 'condition':
                    return `Condition_${nodeCount}`;
                case 'switch':
                    return `Switch_${nodeCount}`;
                case 'each':
                    return `Each_${nodeCount}`;
                default:
                    return `Node_${nodeCount}`;
            }
        },

        getGenericNodeName(nodeType, action) {
            // For event nodes, use the action to determine the name
            if (nodeType === 'event' && action) {
                const nodeCount = this.nodes.filter(n => n.type === nodeType && n.action === action).length + 1;
                switch (action) {
                    case 'custom':
                        return `CustomEvent_${nodeCount}`;
                    case 'create':
                        return `Create_${nodeCount}`;
                    case 'update':
                        return `Update_${nodeCount}`;
                    case 'delete':
                        return `Delete_${nodeCount}`;
                    case 'before_create':
                        return `BeforeCreate_${nodeCount}`;
                    case 'before_update':
                        return `BeforeUpdate_${nodeCount}`;
                    case 'before_delete':
                        return `BeforeDelete_${nodeCount}`;
                    default:
                        return `${action.charAt(0).toUpperCase() + action.slice(1)}_${nodeCount}`;
                }
            }
            
            // For operation nodes, use the action to determine the name
            if (nodeType === 'operation' && action) {
                const nodeCount = this.nodes.filter(n => n.type === nodeType && n.action === action).length + 1;
                switch (action) {
                    case 'expression':
                        return `Expression_${nodeCount}`;
                    case 'operation':
                        return `Operation_${nodeCount}`;
                    default:
                        return `${action.charAt(0).toUpperCase() + action.slice(1)}_${nodeCount}`;
                }
            }
            
            // For other node types, use the type
            const nodeCount = this.nodes.filter(n => n.type === nodeType).length + 1;
            switch (nodeType) {
                case 'event':
                    return `Event_${nodeCount}`;
                case 'operation':
                    return `Operation_${nodeCount}`;
                case 'condition':
                    return `Condition_${nodeCount}`;
                case 'switch':
                    return `Switch_${nodeCount}`;
                case 'each':
                    return `Each_${nodeCount}`;
                default:
                    return `Node_${nodeCount}`;
            }
        },

        getDefaultAction(nodeType) {
            switch (nodeType) {
                case 'event':
                    return 'event';
                case 'operation':
                    return 'operation'; // Default operation action
                case 'condition':
                    return 'condition';
                case 'switch':
                    return 'switch';
                case 'each':
                    return 'each';
                default:
                    return nodeType || 'unknown';
            }
        },

        handleChartSave(nodes, connections) {
            // Add defensive checks for undefined values
            if (!Array.isArray(nodes)) {
                console.error('handleChartSave: nodes is not an array', nodes);
                return;
            }
            if (!Array.isArray(connections)) {
                console.error('handleChartSave: connections is not an array', connections);
                return;
            }
            
            // Filter out any undefined or null nodes
            nodes = nodes.filter(n => n != null);
            connections = connections.filter(c => c != null && c.source && c.destination);
            
            console.log('handleChartSave called with:', {
                nodeCount: nodes.length,
                connectionCount: connections.length,
                shouldSaveToHistory: this._shouldSaveToHistory,
                // Log first few node positions for debugging
                firstNodePositions: nodes.slice(0, 3).map(n => ({ id: n.id, name: n.name, x: n.x, y: n.y }))
            });
            
            nodes.map(n => {
                n.connections = [];

                return n;
            })

            connections.forEach(c => {
                let sourceId = c.source.id

                let node = nodes.find(n => {
                    return n.id == sourceId;
                })

                // Skip if node not found
                if (!node) {
                    console.warn('handleChartSave: Source node not found for connection', { sourceId, connection: c });
                    return;
                }

                // check does connection already exists
                let exist = node.connections.find(nodec => {
                    return nodec.source.id == c.source.id && nodec.destination.id == c.destination.id
                })

                if (!exist) {
                    node.connections.push({
                        source: c.source,
                        destination: c.destination,
                        type: c.type,
                        id: c.id,
                        name: c.name
                    })
                }
            })

            // Update component state with the latest data from FlowChart
            this.nodes = JSON.parse(JSON.stringify(nodes));
            this.connections = JSON.parse(JSON.stringify(connections));
            this.content = JSON.parse(JSON.stringify(nodes));
            
            console.log('handleChartSave: Updated component state', {
                nodeCount: this.nodes.length,
                connectionCount: this.connections.length,
                action: this._shouldSaveToHistory
            });
            
            // Save to history if requested by calling method
            if (this._shouldSaveToHistory) {
                this.saveToHistory(this._shouldSaveToHistory)
            }
            
            this.$emit('save', this.content)
        },

        handleEditNode(node) {
            // In dry run mode, show execution detail for this node
            if (this.playNodeModal && this.playNodeResults.length > 0) {
                let result = this.playNodeResults.find(r => r.id === node.id);
                if (result && result.state) {
                    this.playNodeDetail = result;
                    return;
                }
            }

            // Prevent editing in readonly mode (e.g., extension workflows)
            if (this.readonly) return;

            // node each can't be edited
            if (node.type == "each") return;

            // Close the sidebar when editing a node
            this.sidebarOpen = false;

            // Reset outputs panel state
            this.showEventOutputs = true;

            // Create a deep clone for editing to avoid direct mutations
            const clonedNode = JSON.parse(JSON.stringify(node));

            // Always look up outputs and settings definitions from the definition
            if (clonedNode.type === 'event') {
                var matchedTrigger = this.triggerNodes.find(function(t) {
                    return t.eventName === clonedNode.eventName || t.name === clonedNode.name;
                });
                clonedNode.outputs = (matchedTrigger && matchedTrigger.outputs) ? JSON.parse(JSON.stringify(matchedTrigger.outputs)) : [];
                clonedNode.eventSettingsDef = (matchedTrigger && matchedTrigger.settings) ? JSON.parse(JSON.stringify(matchedTrigger.settings)) : [];
                clonedNode.customSettingsView = (matchedTrigger && matchedTrigger.customSettingsView) ? matchedTrigger.customSettingsView : null;
            }

            this.editNode.data = clonedNode;

            // Initialize node.data.settings for event nodes
            if (clonedNode.type === 'event') {
                if (!clonedNode.data) {
                    this.$set(this.editNode.data, 'data', {});
                }
                if (!this.editNode.data.data.settings) {
                    this.$set(this.editNode.data.data, 'settings', {});
                }
                // Apply defaults for any unset settings
                var settingsDef = this.editNode.data.eventSettingsDef || [];
                for (var i = 0; i < settingsDef.length; i++) {
                    var s = settingsDef[i];
                    if (this.editNode.data.data.settings[s.name] === undefined && s.default !== undefined) {
                        this.$set(this.editNode.data.data.settings, s.name, s.default);
                    }
                }
            }
            // Also set top-level properties for proper two-way binding
            this.editNode.name = clonedNode.name;
            this.editNode.label = clonedNode.label;
            this.editNode.modal = true
        },

        handleEditConnection(conn) {
            // Prevent editing in readonly mode (e.g., extension workflows)
            if (this.readonly) return;
            
            let sourceId = conn.source.id
            let sourceNode = this.nodes.find(n => {
                return n.id == sourceId
            })

            if (sourceNode.type == 'switch') {
                //console.log(connection)
                this.editConnection = conn
                this.editConnectionModal = true
            }
        },

        handleDblClick() {
            // Prevent adding nodes in readonly mode (e.g., extension workflows)
            if (this.readonly) return;
            
            // Open the sidebar to add nodes instead of creating directly
            this.openNodeSidebar();
        },

        handleRemoveNode(node) {
            // Prevent removing nodes in readonly mode (e.g., extension workflows)
            if (this.readonly) return;
            
            const nodeName = node.name || node.label || `${node.type} node`;
            // Set flag to save to history when chart saves
            this._shouldSaveToHistory = `Removed ${nodeName}`;
            this.$refs.chart.remove(node)
            this.$refs.chart.save()
            this._shouldSaveToHistory = null;
        },  

        drawConnection(conn, internalConnections, internalNodes) {
            // Add defensive checks for undefined nodes
            if (!conn || !conn.source || !conn.destination) {
                console.warn('drawConnection: Invalid connection object', conn);
                return false;
            }

            conn.name = null

            let sourceId = conn.source.id
            let destinationId = conn.destination.id;
            
            // Ensure internalNodes is an array
            if (!Array.isArray(internalNodes)) {
                console.error('drawConnection: internalNodes is not an array', internalNodes);
                return false;
            }
            
            let sourceNode = internalNodes.find(n => {
                return n && n.id == sourceId
            })
            let destinationNode = internalNodes.find(n => {
                return n && n.id == destinationId
            })

            if (sourceNode !== undefined && destinationNode !== undefined) {
                // operation can have only one link from same source to same destination
                let existingConnection = internalConnections.find(c => {
                    return c.source.id == sourceId && c.destination.id == destinationId
                })
                if (existingConnection !== undefined) {
                    return false;
                }

                if (sourceNode.type == "condition") {
                    if (conn.source.position == "right") {
                        conn.type = "reject";
                    }
                }

                if (destinationNode.type == "condition") {
                    if (conn.destination.position !== "top") {
                        return false
                    }
                }

                // event can't be connected to event
                if (sourceNode.type == "event" && destinationNode.type == "event") {
                    return false
                }

                // operation can't be connected to event
                if (sourceNode.type == "operation" && destinationNode.type == "event") {
                    return false
                }

                if (sourceNode.type == "condition" && destinationNode.type == "event") {
                    return false
                }

                // circular connections
                if (sourceNode.type == "operation" && destinationNode.type == "operation") {
                    let sourceConnection = internalConnections.find(c => {
                        return c.source.id == sourceNode.id && c.destination.id == sourceNode.id
                    })

                    if (sourceConnection !== undefined) {
                        return false;
                    }
                }
            }

            return true;
        },

        createConnection(conn, internalConnections, internalNodes) {
            let drawn = this.drawConnection(conn, internalConnections, internalNodes)
            if (drawn) {
                internalConnections.push(conn)
                // Call handleChartSave but with a flag to indicate this should save to history
                this._shouldSaveToHistory = 'Connection Created'
                this.handleChartSave(internalNodes, internalConnections)
                this._shouldSaveToHistory = null
            }

            return drawn
        },

        dragend: _.debounce(function() {
            console.log('Drag ended, saving positions to history');
            // Set flag to save to history when chart saves
            this._shouldSaveToHistory = 'Node Positions Changed'
            // Force the chart to save its current state
            this.$refs.chart.save()
            this._shouldSaveToHistory = null
        }, 1500),

        newNodeAdd(e) {
            if (e) e.preventDefault();

            let node = {
                ...this.newNode.data,
                id: _.random(10000000, 19999999),
            };

            if (node.type != 'each' && (node.name == '' || node.type == '')) {
                return;
            }

            // For event nodes, construct full event name with schema prefix
            if (node.type === 'event' && node.schema && node.name) {
                // Store original name without schema
                node.eventName = node.name;
                // Construct full name with schema prefix
                node.name = `${node.schema}::${node.eventName}`;
            } else if (node.type === 'event') {
                // Use default schema if none provided
                const defaultSchema = this.schema || '';
                if (defaultSchema && node.name) {
                    node.eventName = node.name;
                    node.name = `${defaultSchema}::${node.eventName}`;
                }
            }

            this.$refs.chart.add(node)

            this.newNode.data = {
                name: "",
                schema: "",
                type: "",
                x: 0,
                y: 0,
                action: "",
                connections: [],
                data: {}
            }

            this.$refs.chart.save()
            this.closeAddNodeDrawer();
        },

        nodeEdit() {
            let editedNode = this.editNode.data

            let nodes = this.nodes.map(n => {
                if (n.id == editedNode.id) {
                    n = editedNode
                }

                return n
            })

            this.$emit("update:nodes", nodes)
        },

        closeNodeEdit(e) {
           if (this.checkForErrors()) {
               e.preventDefault()
               this.$mainDialog.msgBoxOk("Some fields are not valid!")
           } else {
               // Persist edits made in the modal by triggering FlowChart save,
               // which propagates nodes to parent via handleChartSave -> this.content
               if (this.$refs.chart && typeof this.$refs.chart.save === 'function') {
                   this.$refs.chart.save()
               }
           }
        },

        // Drawer methods
        closeAddNodeDrawer() {
            this.newNode.modal = false;
        },

        closeEditNodeDrawer() {
            this.editNode.modal = false;
            // Reset drag position when closing
            this.isDragging = false;
            this.popoverX = 0;
            this.popoverY = 0;
        },

        toggleEventOutputs() {
            this.showEventOutputs = !this.showEventOutputs;
        },

        startDrag(event) {
            this.isDragging = true;
            this.dragStartX = event.clientX;
            this.dragStartY = event.clientY;

            if (this.popoverX === 0 && this.popoverY === 0) {
                let el = event.target.closest('.node-edit-popover');
                if (el) {
                    let rect = el.getBoundingClientRect();
                    this.popoverX = rect.left;
                    this.popoverY = rect.top;
                }
            }

            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.stopDrag);
        },

        onDrag(event) {
            if (!this.isDragging) return;
            
            const deltaX = event.clientX - this.dragStartX;
            const deltaY = event.clientY - this.dragStartY;
            
            this.popoverX += deltaX;
            this.popoverY += deltaY;
            
            this.dragStartX = event.clientX;
            this.dragStartY = event.clientY;
        },

        stopDrag() {
            this.isDragging = false;
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.stopDrag);
        },

        startResize(event) {
            this.isResizing = true;
            this.dragStartX = event.clientX;
            this.dragStartY = event.clientY;
            
            document.addEventListener('mousemove', this.onResize);
            document.addEventListener('mouseup', this.stopResize);
            event.stopPropagation();
        },

        onResize(event) {
            if (!this.isResizing) return;
            
            const deltaX = event.clientX - this.dragStartX;
            const deltaY = event.clientY - this.dragStartY;
            
            this.popoverWidth = Math.max(400, this.popoverWidth + deltaX);
            this.popoverHeight = Math.max(250, this.popoverHeight + deltaY);
            
            this.dragStartX = event.clientX;
            this.dragStartY = event.clientY;
        },

        stopResize() {
            this.isResizing = false;
            document.removeEventListener('mousemove', this.onResize);
            document.removeEventListener('mouseup', this.stopResize);
        },

        resetPopoverSize() {
            this.popoverWidth = 700;
            this.popoverHeight = 550;
            this.popoverX = 0;
            this.popoverY = 0;
        },

        saveNodeEdit() {
            if (this.checkForErrors()) {
                this.$mainDialog.msgBoxOk("Some fields are not valid!")
                return;
            }

            // For event nodes, construct full event name with schema prefix
            if (this.editNode.data.type === 'event' && this.editNode.data.schema) {
                // Extract the event name without schema (in case it was already prefixed)
                const eventName = this.editNode.data.name.includes('::') 
                    ? this.editNode.data.name.split('::').pop() 
                    : this.editNode.data.name;
                
                // Store original name without schema
                this.editNode.data.eventName = eventName;
                // Construct full name with schema prefix
                this.editNode.data.name = `${this.editNode.data.schema}::${eventName}`;
            }

            // For custom events, clear the label so the name is displayed instead
            if (this.editNode.data.type === 'event' && this.editNode.data.action === 'custom') {
                this.editNode.data.label = null;
                // Also clear the nested data.label
                if (this.editNode.data.data) {
                    this.editNode.data.data.label = null;
                }
            }

            // Update the node in the nodes array
            const nodeToSave = JSON.parse(JSON.stringify(this.editNode.data));
            // Strip definition-derived fields — they are looked up at display time, not stored
            delete nodeToSave.outputs;
            const nodesArrayIndex = this.nodes.findIndex(n => n.id === this.editNode.data.id);
            if (nodesArrayIndex !== -1) {
                this.$set(this.nodes, nodesArrayIndex, nodeToSave);
            }

            // Update content and save to history directly
            this.content = JSON.parse(JSON.stringify(this.nodes));
            const nodeName = this.editNode.data.name || this.editNode.data.label || `${this.editNode.data.type} node`;
            this.saveToHistory(`Edited ${nodeName}`);
            this.$emit('save', this.content);
            
            this.closeEditNodeDrawer();
        },

        fieldError(error) {
            let found = this.errors.find((i) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        checkForErrors() {
            return this.errors.find((i) => {
                return i.error
            });
        },

        recycleExprInput: _.debounce(function(code) {
            if (code === "") return;

            try {
                this.recycleExprErrors = [];
                JSON.parse(code);
            } catch(e) {
                this.recycleExprErrors.push(e)
            }

        }, 1000),

        dryRun(event) {
            this.dryRunInputModal = false

            if (!event) {
                event = this.dryRunEvent
            }

            let data = {
                component_uuid: this.uuid,
                event: event,
                dry_run: true,
                nodes: this.content,
            }

            if (this.dryRunMockedModel) {
                data.model_collection = [this.dryRunMockedModel]
            }

            let popup = this.$loadingPopup("Collecting results...")
            this.$workflowApi.post("/workflow/component/dryrun", data).then(resp => {
                this.showDryRunResults = true
                this.dryRunResults = resp.data
                this.nodes = this.content
                this.version++
            }).catch(err => {
                this.showDryRunResults = true
                this.dryRunResults = err.response.data
                this.nodes = this.content
                this.version++
                
                this.$debugLog(err)
            }).finally(() => {
                popup.hide()
            })
        },

        attemptDryRun(event) {
            if (event == "Recycle") {
               return this.dryRun(event)
            }

            this.dryRunInputModal = true
            this.dryRunEvent = event
        },

        showResultsForNode(results) {
            this.inspectResult = results
            this.resultsSidebar = true
        },

        onDryInput: _.debounce(function(code) {
            if (code === "") return;

            try {
                this.dryRunInputErrors = [];
                JSON.parse(code);
            } catch(e) {
                this.dryRunInputErrors.push(e)
            }

        }, 1000),

        handlePlayNode(node) {
            this.playNodeTarget = node;
            this.playNodeInput = '{}';
            this.playNodeInputErrors = [];
            this.playNodeResults = [];
            this.playNodeExecutionTime = null;
            this.playNodeExecutionUUID = null;
            this.playNodeStatus = null;
            this.playNodeModal = true;

            this.$nextTick(() => {
                if (this.$refs.chart) {
                    this.$refs.chart.focusOnNode(node, true);
                }
            });
        },

        onPlayNodeInput: _.debounce(function(code) {
            if (code === "") return;
            try {
                this.playNodeInputErrors = [];
                JSON.parse(code);
            } catch(e) {
                this.playNodeInputErrors.push(e);
            }
        }, 500),

        closePlayNode() {
            this.stopPlayNodePolling();
            this.playNodeModal = false;
            this.playNodeTarget = null;
            this.playNodeDetail = null;
            this.detailSearchQuery = '';
            this.playNodeResults = [];
            this.playNodeExecutionTime = null;
            this.playNodeExecutionUUID = null;
            this.playNodeStatus = null;
            this.playPopoverX = 0;
            this.playPopoverY = 0;
            this.detailPopoverX = 0;
            this.detailPopoverY = 0;
            if (this.$refs.chart) {
                this.$refs.chart.clearFocus();
            }
        },

        startPlayDrag(event) {
            this.isPlayDragging = true;
            this.playDragStartX = event.clientX;
            this.playDragStartY = event.clientY;

            if (this.playPopoverX === 0 && this.playPopoverY === 0) {
                let el = event.target.closest('.play-node-popover');
                if (el) {
                    let rect = el.getBoundingClientRect();
                    this.playPopoverX = rect.left;
                    this.playPopoverY = rect.top;
                }
            }

            document.addEventListener('mousemove', this.onPlayDrag);
            document.addEventListener('mouseup', this.onPlayDragEnd);
        },

        onPlayDrag(event) {
            if (!this.isPlayDragging) return;
            let dx = event.clientX - this.playDragStartX;
            let dy = event.clientY - this.playDragStartY;
            this.playPopoverX += dx;
            this.playPopoverY += dy;
            this.playDragStartX = event.clientX;
            this.playDragStartY = event.clientY;
        },

        onPlayDragEnd() {
            this.isPlayDragging = false;
            document.removeEventListener('mousemove', this.onPlayDrag);
            document.removeEventListener('mouseup', this.onPlayDragEnd);
        },

        startPlayResize(event) {
            this.isPlayResizing = true;
            this.playResizeStartX = event.clientX;
            this.playResizeStartY = event.clientY;
            document.addEventListener('mousemove', this.onPlayResize);
            document.addEventListener('mouseup', this.onPlayResizeEnd);
        },

        onPlayResize(event) {
            if (!this.isPlayResizing) return;
            let dx = event.clientX - this.playResizeStartX;
            let dy = event.clientY - this.playResizeStartY;
            this.playPopoverWidth = Math.max(400, this.playPopoverWidth + dx);
            this.playPopoverHeight = Math.max(300, this.playPopoverHeight + dy);
            this.playResizeStartX = event.clientX;
            this.playResizeStartY = event.clientY;
        },

        onPlayResizeEnd() {
            this.isPlayResizing = false;
            document.removeEventListener('mousemove', this.onPlayResize);
            document.removeEventListener('mouseup', this.onPlayResizeEnd);
        },

        startDetailDrag(event) {
            this.isDetailDragging = true;
            this.detailDragStartX = event.clientX;
            this.detailDragStartY = event.clientY;

            if (this.detailPopoverX === 0 && this.detailPopoverY === 0) {
                let el = event.target.closest('.node-detail-popover');
                if (el) {
                    let rect = el.getBoundingClientRect();
                    this.detailPopoverX = rect.left;
                    this.detailPopoverY = rect.top;
                }
            }

            document.addEventListener('mousemove', this.onDetailDrag);
            document.addEventListener('mouseup', this.onDetailDragEnd);
        },

        onDetailDrag(event) {
            if (!this.isDetailDragging) return;
            let dx = event.clientX - this.detailDragStartX;
            let dy = event.clientY - this.detailDragStartY;
            this.detailPopoverX += dx;
            this.detailPopoverY += dy;
            this.detailDragStartX = event.clientX;
            this.detailDragStartY = event.clientY;
        },

        onDetailDragEnd() {
            this.isDetailDragging = false;
            document.removeEventListener('mousemove', this.onDetailDrag);
            document.removeEventListener('mouseup', this.onDetailDragEnd);
        },

        startDetailResize(event) {
            this.isDetailResizing = true;
            this.detailResizeStartX = event.clientX;
            this.detailResizeStartY = event.clientY;
            document.addEventListener('mousemove', this.onDetailResize);
            document.addEventListener('mouseup', this.onDetailResizeEnd);
        },

        onDetailResize(event) {
            if (!this.isDetailResizing) return;
            let dx = event.clientX - this.detailResizeStartX;
            let dy = event.clientY - this.detailResizeStartY;
            this.detailPopoverWidth = Math.max(400, this.detailPopoverWidth + dx);
            this.detailPopoverHeight = Math.max(300, this.detailPopoverHeight + dy);
            this.detailResizeStartX = event.clientX;
            this.detailResizeStartY = event.clientY;
        },

        onDetailResizeEnd() {
            this.isDetailResizing = false;
            document.removeEventListener('mousemove', this.onDetailResize);
            document.removeEventListener('mouseup', this.onDetailResizeEnd);
        },

        stopPlayNodePolling() {
            if (this.playNodePollTimer) {
                clearInterval(this.playNodePollTimer);
                this.playNodePollTimer = null;
            }
        },

        executePlayNode() {
            if (this.playNodeInputErrors.length > 0) return;
            if (!this.playNodeTarget) return;

            let input = {};
            try {
                input = JSON.parse(this.playNodeInput || '{}');
            } catch(e) {
                this.playNodeInputErrors.push(e);
                return;
            }

            this.playNodeRunning = true;
            this.playNodeResults = [];
            this.playNodeExecutionTime = null;
            this.playNodeExecutionUUID = null;
            this.playNodeStatus = 'starting';
            this.stopPlayNodePolling();

            let startTime = Date.now();
            const historyService = new WorkflowHistory(this.$workflowApi);

            let data = {
                component_uuid: this.uuid,
                start_node_id: this.playNodeTarget.id,
                dry_run: true,
                input: input,
                nodes: this.content,
            };

            this.$workflowApi.post("/workflow/component/dryrun", data).then(resp => {
                const executionUUID = resp.data.execution_uuid;
                if (!executionUUID) {
                    this.playNodeRunning = false;
                    this.playNodeResults = [resp.data];
                    return;
                }

                this.playNodeExecutionUUID = executionUUID;
                this.playNodeStatus = 'running';

                this.playNodePollTimer = setInterval(async () => {
                    try {
                        const execution = await historyService.fetchExecutionDetailsAuto(executionUUID);
                        this.playNodeExecutionTime = Date.now() - startTime;
                        this.playNodeStatus = execution.status;

                        const isTerminal = execution.status === 'completed' || execution.status === 'errored' || execution.status === 'killed';
                        if (isTerminal) {
                            this.stopPlayNodePolling();
                            this.playNodeRunning = false;
                        }

                        if (execution.execution_nodes) {
                            this.playNodeResults = execution.execution_nodes
                                .filter(en => en.node)
                                .map(en => {
                                    const node = en.node;
                                    if (!node.state && node.State) node.state = node.State;
                                    return node;
                                });

                            if (this.$refs.chart) {
                                execution.execution_nodes.forEach(en => {
                                    if (!en.node) return;
                                    const nodeState = en.node.state || en.node.State;
                                    const nodeId = en.node.id || en.node.ID;
                                    if (nodeState && nodeState.status && typeof nodeId === 'number') {
                                        this.$refs.chart.updateNodeDebugStatus(nodeId, nodeState.status);
                                    }
                                });
                            }
                        }
                    } catch (err) {
                        console.error('Polling error:', err);
                    }
                }, 3000);
            }).catch(err => {
                this.playNodeExecutionTime = Date.now() - startTime;
                this.playNodeRunning = false;
                this.playNodeStatus = 'errored';
                if (err.response && err.response.data) {
                    this.playNodeResults = Array.isArray(err.response.data) ? err.response.data : [err.response.data];
                }
            });
        },

        getNodeStatusVariant(status) {
            switch(status) {
                case 'completed': return 'success';
                case 'errored': return 'danger';
                case 'running': return 'primary';
                case 'skipped': return 'secondary';
                case 'pending': return 'warning';
                default: return 'light';
            }
        },

        eventNameReducer: (o) => {
            const { name, label } = o
            if (!name && !label) return o

            if (!name) {
                return label
            }

            return name
        },

        getEventIcon(eventName) {
            const iconMap = {
                'Create': '➕',
                'Update': '✏️',
                'Delete': '🗑️',
                'Before::Create': '⏸️',
                'Before::Update': '⏸️',
                'Before::Delete': '⏸️',
                'Recycle': '♻'
            };
            return iconMap[eventName] || '⚡';
        },

        getEventColor(eventName) {
            const colorMap = {
                'Create': '#10b981',
                'Update': '#f59e0b',
                'Delete': '#ef4444',
                'Before::Create': '#06b6d4',
                'Before::Update': '#8b5cf6',
                'Before::Delete': '#f97316',
                'Recycle': '#06b6d4'
            };
            return colorMap[eventName] || '#6366f1';
        },

        enrichNodesWithTranslations(nodes) {
            // Update event node labels with current translations
            nodes.forEach(node => {
                if (node.type === 'event' && node.eventName) {
                    // Find the matching event from eventNames prop
                    const matchingEvent = this.eventNames.find(e => e.name === node.eventName);
                    if (matchingEvent && matchingEvent.label) {
                        // Translate the label - handle both object and string formats
                        let translatedLabel;
                        if (typeof matchingEvent.label === 'object' && matchingEvent.label.locales) {
                            translatedLabel = this.$translateLabel(matchingEvent.label, matchingEvent.name);
                        } else if (typeof matchingEvent.label === 'string') {
                            // Already translated, use as-is
                            translatedLabel = matchingEvent.label;
                        } else {
                            translatedLabel = matchingEvent.name;
                        }
                        
                        node.label = translatedLabel;
                        if (node.data) {
                            node.data.label = translatedLabel;
                        }
                    }
                }
            });
        }
    },

    beforeDestroy() {
        this.stopPlayNodePolling();
        document.removeEventListener('mousemove', this.onPlayDrag);
        document.removeEventListener('mouseup', this.onPlayDragEnd);
        document.removeEventListener('mousemove', this.onPlayResize);
        document.removeEventListener('mouseup', this.onPlayResizeEnd);
        document.removeEventListener('mousemove', this.onDetailDrag);
        document.removeEventListener('mouseup', this.onDetailDragEnd);
        document.removeEventListener('mousemove', this.onDetailResize);
        document.removeEventListener('mouseup', this.onDetailResizeEnd);
        document.removeEventListener('keydown', this.handleKeyDown)
        // Unregister kortex workflow canvas tools
        if (window.kortex) {
            window.kortex.unregisterContext()
            window.kortex.hideHelper()
            window.kortex.unregisterTool('get_workflow_nodes')
            window.kortex.unregisterTool('get_available_node_types')
            window.kortex.unregisterTool('add_workflow_node')
            window.kortex.unregisterTool('remove_workflow_node')
            window.kortex.unregisterTool('connect_workflow_nodes')
            window.kortex.unregisterTool('set_node_data')
            window.kortex.unregisterTool('save_workflow')
            window.kortex.unregisterTool('clear_workflow_canvas')
        }
    },

    mounted() {
        this.nodes = this.content || []
        
        // Enrich nodes with translated labels
        this.enrichNodesWithTranslations(this.nodes);

        let connections = [];
        this.nodes.forEach(n => {
            let c = JSON.parse(JSON.stringify(n))
            if (c.connections) {
                connections = connections.concat(c.connections)
            }
        })

        this.connections = connections;

        if (this.$store.getters.isLiveEnv) {
            document.onkeyup = function(e) {
                console.log(e)
                if (e.code == 'Delete') {
                    e.preventDefault();
                    e.stopImmediatePropagation()
                }
            }
        }
        
        // Add keyboard shortcuts for history
        this.handleKeyDown = (e) => {
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'z' && !e.shiftKey) {
                    e.preventDefault();
                    this.undo();
                } else if ((e.key === 'y') || (e.key === 'z' && e.shiftKey)) {
                    e.preventDefault();
                    this.redo();
                }
            }
        };
        document.addEventListener('keydown', this.handleKeyDown)

        console.log("events", this.eventNames)
        this.loadSatelliteWorkflowNodes()
        
        // Load history from localStorage
        console.log('Events component mounted with UUID:', this.uuid, 'Initial nodes:', this.nodes.length);
        this.loadHistoryFromStorage()
        
        // Save initial state to history if workflow has content and no history exists
        if (this.nodes.length > 0 && this.history.length === 0) {
            console.log('Saving initial workflow state to history');
            this.saveToHistory('Workflow Loaded')
        }

        // Register kortex AI tools for workflow canvas editing (dev mode only)
        if (window.kortex && !this.readonly) {
            // Detect workflow context: component workflows have the 'schema' prop set (bound to a data model);
            // project workflows do not have 'schema' and receive integration/platform events via 'eventNames' prop.
            var isComponentWorkflow = !!(this.schema)

            window.kortex.showHelper()

            if (isComponentWorkflow) {
                window.kortex.registerContext(
                    'Component Workflow Editor',
                    'You are editing a COMPONENT workflow. This workflow is bound to a specific data model (component). ' +
                    'Available triggers are model lifecycle events ONLY: Create, Before::Create, Update, Before::Update, Delete, Before::Delete, plus custom events. ' +
                    'Integration events and platform events are NOT available in component workflows. ' +
                    'Use get_available_node_types to see all available nodes, add_workflow_node to add them, connect_workflow_nodes to wire them, set_node_data to configure them, and save_workflow to persist changes. ' +
                    'DATA ACCESS — RULES YOU MUST FOLLOW: ' +
                    'ALWAYS call get_available_node_types first. ONLY use field names that are explicitly listed in the outputs array of the relevant trigger or operation node. NEVER infer, guess, or assume field names — if a field is not listed in outputs, it does not exist. ' +
                    'Syntax: (1) Trigger event outputs: {{input.fieldName}} where fieldName is from triggers[].outputs[].name. ' +
                    '(2) Previously executed node outputs: {{node.NodeName.fieldName}} where NodeName is the exact label of the node and fieldName is from that node\'s outputs[].name. ' +
                    '(3) In JS expression nodes: io.getPipedDataOf("NodeName") returns the output object; io.pipe(data) sends output downstream.',
                    'fa-sitemap'
                )
            } else {
                window.kortex.registerContext(
                    'Workflow Editor',
                    'You are in the PROJECT workflow editor. Project workflows are event-driven automation pipelines. ' +
                    'PROJECT workflows are triggered by: custom events, integration events (from connected integrations), or platform events (e.g. platform.user.invite.sent). ' +
                    'Model CRUD events (Create/Update/Delete) are NOT available in project workflows — those belong to component workflows only. ' +
                    'Each workflow has trigger nodes (events), operation nodes (expressions, integrations), and logic nodes (conditions, switches, loops). ' +
                    'Use get_workflow_nodes to see the current canvas, get_available_node_types to see what you can add (check workflow_context), ' +
                    'add_workflow_node to add nodes, connect_workflow_nodes to wire them, set_node_data to configure them, and save_workflow to persist changes. ' +
                    'DATA ACCESS — RULES YOU MUST FOLLOW: ' +
                    'ALWAYS call get_available_node_types first. ONLY use field names that are explicitly listed in the outputs array of the relevant trigger or operation node. NEVER infer, guess, or assume field names — if a field is not listed in outputs, it does not exist. ' +
                    'Syntax: (1) Trigger event outputs: {{input.fieldName}} where fieldName is from triggers[].outputs[].name. ' +
                    '(2) Previously executed node outputs: {{node.NodeName.fieldName}} where NodeName is the exact label of the node and fieldName is from that node\'s outputs[].name. ' +
                    '(3) In JS expression nodes: io.getPipedDataOf("NodeName") returns the output object; io.pipe(data) sends output downstream.',
                    'fa-sitemap'
                )
            }

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'get_workflow_nodes',
                        description: 'Returns the current workflow canvas state: all nodes with IDs, names, types, actions, data, connections, and outputs. ' +
                            'outputs: predefined output fields for integration/event nodes (access via io.getPipedDataOf("NodeName").fieldName). ' +
                            'Expression/condition/switch/each nodes output whatever you pass to io.pipe(data) — no predefined outputs. ' +
                            'Call this first to understand what is already on the canvas before making changes.',
                        parameters: { type: 'object', properties: {} }
                    }
                },
                () => {
                    return {
                        nodes: this.nodes.map(n => ({
                            id: n.id,
                            name: n.name,
                            type: n.type,
                            action: n.action,
                            label: n.label,
                            paused: n.paused,
                            data: n.data,
                            connections: (n.connections || []).map(c => ({
                                source_id: c.source && c.source.id,
                                destination_id: c.destination && c.destination.id,
                                type: c.type || 'default'
                            })),
                            outputs: n.outputs && n.outputs.length > 0
                                ? n.outputs.map(o => ({ name: o.name, type: o.type }))
                                : null
                        })),
                        total_nodes: this.nodes.length,
                        connections_count: this.connections.length,
                        io_api: {
                            description: 'The io object available in all expression/condition/switch nodes',
                            methods: [
                                { name: 'io.getPipedDataOf(nodeName)', description: 'Get piped output from a specific upstream node by its name. Returns the object passed to io.pipe() in that node.' },
                                { name: 'io.getPipedData()', description: 'Get piped output from the direct predecessor node.' },
                                { name: 'io.pipe(data)', description: 'Pass data to downstream nodes. Downstream nodes access it via io.getPipedDataOf(thisNodeName).' },
                                { name: 'io.each(arrayData)', description: 'Pass an array to an each (loop) node connected after this one.' },
                                { name: 'io.cursor()', description: 'Inside a loop body: get the current iteration item from the each node.' }
                            ]
                        }
                    }
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'get_available_node_types',
                        description: 'Returns all node types available to add to the workflow, grouped into triggers (events), operations, and logic. ' +
                            'workflow_context: "component" = only model lifecycle events + custom; "project" = only integration/platform/custom events. ' +
                            'Each trigger and integration operation includes an outputs array listing the fields they produce (name + type). ' +
                            'Access those fields in downstream expressions via io.getPipedDataOf("NodeName").fieldName. ' +
                            'Expression/condition/switch/each nodes have no predefined outputs — they output whatever you pass to io.pipe(data). ' +
                            'Use this before add_workflow_node to see valid type/action/event_name combinations.',
                        parameters: { type: 'object', properties: {} }
                    }
                },
                () => {
                    return {
                        workflow_context: isComponentWorkflow ? 'component' : 'project',
                        triggers: this.triggerNodes.map(n => ({
                            type: n.type,
                            action: n.action,
                            event_name: n.eventName || n.name,
                            label: n.label,
                            description: n.description,
                            integration: n._integration ? n._integration.integration_label : null,
                            outputs: n.outputs && n.outputs.length > 0
                                ? n.outputs.map(o => ({ name: o.name, type: o.type }))
                                : null
                        })),
                        operations: this.actionNodes.map(n => ({
                            type: n.type,
                            action: n.action,
                            label: n.label,
                            description: n.description,
                            integration_id: n._integration ? n._integration.integration_id : null,
                            satellite_id: n._integration ? n._integration.satellite_id || null : null,
                            node_name: n._integration ? n._integration.node_name : null,
                            inputs: n._integration && n._integration.inputs ? n._integration.inputs.map(i => ({ name: i.name, type: i.type, description: i.description })) : null,
                            outputs: n._integration && n._integration.outputs && n._integration.outputs.length > 0
                                ? n._integration.outputs.map(o => ({ name: o.name, type: o.type }))
                                : null
                        })),
                        logic: this.logicNodes.map(n => ({
                            type: n.type,
                            action: n.action,
                            label: n.label,
                            description: n.description,
                            outputs: 'dynamic — outputs whatever you pass to io.pipe(data); access via io.getPipedDataOf("NodeName")'
                        })),
                        io_api: {
                            description: 'The io object available in all expression/condition/switch nodes',
                            methods: [
                                { name: 'io.getPipedDataOf(nodeName)', description: 'Get piped output from a specific upstream node by its name. Returns the object passed to io.pipe() in that node.' },
                                { name: 'io.getPipedData()', description: 'Get piped output from the direct predecessor node.' },
                                { name: 'io.pipe(data)', description: 'Pass data to downstream nodes. Downstream nodes access it via io.getPipedDataOf(thisNodeName).' },
                                { name: 'io.each(arrayData)', description: 'Pass an array to an each (loop) node connected after this one.' },
                                { name: 'io.cursor()', description: 'Inside a loop body: get the current iteration item from the each node.' }
                            ]
                        }
                    }
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'add_workflow_node',
                        description: 'Adds a node to the workflow canvas. ' +
                            'In a COMPONENT workflow (workflow_context="component"): use event triggers like event_name="Create", "Update", "Delete", "Before::Create", "Before::Update", "Before::Delete", or action="custom". Integration/platform events are NOT available. ' +
                            'In a PROJECT workflow (workflow_context="project"): use integration events or platform events (e.g. event_name="platform.user.invite.sent") or action="custom". Model CRUD events (Create/Update/Delete) are NOT available. ' +
                            'For JS expression operations: type="operation", action="expression". For integration operations: type="operation", action="integration" with integration_id and node_name from get_available_node_types. For logic nodes: type="condition", "switch", or "each". Returns the new node ID — use it in connect_workflow_nodes.',
                        parameters: {
                            type: 'object',
                            required: ['type'],
                            properties: {
                                type: { type: 'string', enum: ['event', 'operation', 'condition', 'switch', 'each'], description: 'Node category' },
                                action: { type: 'string', description: 'Node action. For events: "custom" or the action slug. For operations: "expression" or "integration". Logic nodes derive action from type automatically.' },
                                event_name: { type: 'string', description: 'For event nodes: the full event name, e.g. "platform.user.invite.sent"' },
                                integration_id: { type: 'string', description: 'For integration operation nodes: integration template ID from get_available_node_types' },
                                node_name: { type: 'string', description: 'For integration operation nodes: node name within the integration from get_available_node_types' },
                                x: { type: 'number', description: 'Canvas X position in pixels. Auto-positioned if omitted.' },
                                y: { type: 'number', description: 'Canvas Y position in pixels. Auto-positioned if omitted.' }
                            }
                        }
                    }
                },
                (args) => {
                    var allTypes = [].concat(this.triggerNodes, this.actionNodes, this.logicNodes)
                    var nodeType = null

                    if (args.type === 'event') {
                        if (args.event_name) {
                            nodeType = allTypes.find(n => n.type === 'event' && (n.eventName === args.event_name || n.name === args.event_name))
                        }
                        if (!nodeType && args.action) {
                            nodeType = allTypes.find(n => n.type === 'event' && n.action === args.action)
                        }
                        if (!nodeType) {
                            nodeType = allTypes.find(n => n.type === 'event' && n.action === 'custom')
                        }
                    } else if (args.type === 'operation') {
                        if (args.action === 'integration' && args.integration_id && args.node_name) {
                            nodeType = allTypes.find(n =>
                                n.type === 'operation' &&
                                n.action === 'integration' &&
                                n._integration &&
                                n._integration.integration_id === args.integration_id &&
                                n._integration.node_name === args.node_name
                            )
                        } else {
                            nodeType = allTypes.find(n => n.type === 'operation' && n.action === (args.action || 'expression'))
                        }
                    } else {
                        nodeType = allTypes.find(n => n.type === args.type)
                    }

                    if (!nodeType) {
                        return { error: 'Node type not found. Use get_available_node_types to see available options.' }
                    }

                    // Auto-position: stack vertically below existing nodes
                    var x = args.x !== undefined ? args.x : 150
                    var y = args.y !== undefined ? args.y : 100 + this.nodes.length * 130
                    // createNodeAtPosition subtracts 100/40 from given coords to center the node
                    this.createNodeAtPosition(nodeType, x + 100, y + 40)

                    var newNode = this.nodes[this.nodes.length - 1]
                    return {
                        node_id: newNode.id,
                        name: newNode.name,
                        type: newNode.type,
                        action: newNode.action,
                        message: 'Node added. Use set_node_data to configure it and connect_workflow_nodes to wire it up.'
                    }
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'remove_workflow_node',
                        description: 'Removes a node from the workflow canvas by ID or name. Also removes all connections involving that node.',
                        parameters: {
                            type: 'object',
                            properties: {
                                node_id: { type: 'number', description: 'Node ID (preferred, from get_workflow_nodes or add_workflow_node)' },
                                node_name: { type: 'string', description: 'Node name (used if node_id not provided)' }
                            }
                        }
                    }
                },
                (args) => {
                    var node = null
                    if (args.node_id !== undefined) {
                        node = this.nodes.find(n => n.id == args.node_id)
                    }
                    if (!node && args.node_name) {
                        node = this.nodes.find(n => n.name === args.node_name)
                    }
                    if (!node) {
                        return { error: 'Node not found', available: this.nodes.map(n => ({ id: n.id, name: n.name })) }
                    }
                    var nodeId = node.id
                    var nodeName = node.name
                    this.nodes = this.nodes.filter(n => n.id !== nodeId)
                    this.connections = this.connections.filter(c => !(c.source && c.source.id === nodeId) && !(c.destination && c.destination.id === nodeId))
                    this.content = JSON.parse(JSON.stringify(this.nodes))
                    this.version++
                    this.saveToHistory('Removed ' + nodeName)
                    this.$emit('save', this.content)
                    return { removed: true, node_id: nodeId, name: nodeName }
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'connect_workflow_nodes',
                        description: 'Creates a directional connection between two nodes. Events connect to operations/logic; operations/logic connect to other operations/logic. For condition nodes the bottom port is the "approve" path; use source_position="right" for the "reject" path.',
                        parameters: {
                            type: 'object',
                            required: ['source_id', 'destination_id'],
                            properties: {
                                source_id: { type: 'number', description: 'Source node ID' },
                                destination_id: { type: 'number', description: 'Destination node ID' },
                                source_name: { type: 'string', description: 'Source node name (used if source_id not provided)' },
                                destination_name: { type: 'string', description: 'Destination node name (used if destination_id not provided)' },
                                source_position: { type: 'string', enum: ['bottom', 'right'], description: 'Which port to use on the source. Default "bottom". Use "right" for the reject branch of a condition node.' }
                            }
                        }
                    }
                },
                (args) => {
                    var srcNode = null
                    if (args.source_id !== undefined) {
                        srcNode = this.nodes.find(n => n.id == args.source_id)
                    }
                    if (!srcNode && args.source_name) {
                        srcNode = this.nodes.find(n => n.name === args.source_name)
                    }
                    var dstNode = null
                    if (args.destination_id !== undefined) {
                        dstNode = this.nodes.find(n => n.id == args.destination_id)
                    }
                    if (!dstNode && args.destination_name) {
                        dstNode = this.nodes.find(n => n.name === args.destination_name)
                    }
                    if (!srcNode) return { error: 'Source node not found', available: this.nodes.map(n => ({ id: n.id, name: n.name })) }
                    if (!dstNode) return { error: 'Destination node not found', available: this.nodes.map(n => ({ id: n.id, name: n.name })) }

                    var srcPosition = args.source_position || 'bottom'
                    var conn = {
                        id: Date.now(),
                        name: null,
                        source: { id: srcNode.id, position: srcPosition },
                        destination: { id: dstNode.id, position: 'top' },
                        type: srcPosition === 'right' ? 'reject' : null
                    }
                    var connections = JSON.parse(JSON.stringify(this.connections))
                    var nodes = JSON.parse(JSON.stringify(this.nodes))
                    var drawn = this.createConnection(conn, connections, nodes)
                    if (!drawn) {
                        return { error: 'Connection invalid. Possible reasons: duplicate connection, event-to-event not allowed, operation-to-event not allowed, circular connection not allowed.' }
                    }
                    return { connected: true, source: srcNode.name, destination: dstNode.name, type: conn.type || 'default' }
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'set_node_data',
                        description: 'Updates the configuration data of a workflow node. ' +
                            'DATA ACCESS RULES (MUST FOLLOW): ' +
                            'For trigger event outputs use {{input.fieldName}} — fieldName MUST be from the trigger node\'s outputs array (call get_available_node_types first). ' +
                            'For upstream node outputs use {{node.NodeName.fieldName}} — NodeName is the EXACT label of a node, fieldName MUST be from that node\'s outputs array. ' +
                            'NEVER invent field names. For expression/condition/switch nodes: { "expression": "javascript code" }. ' +
                            'For integration nodes: { "inputs": { "field": "value or {{template}}" } }. ' +
                            'For event nodes: { "schema": "schemaName" }. Data is merged into existing node data.',
                        parameters: {
                            type: 'object',
                            required: ['data'],
                            properties: {
                                node_id: { type: 'number', description: 'Node ID from get_workflow_nodes or add_workflow_node (preferred)' },
                                node_name: { type: 'string', description: 'Node name (used if node_id not provided)' },
                                data: {
                                    type: 'object',
                                    description: 'Config to apply. ' +
                                        'For integration nodes: { "inputs": { "fieldName": "{{node.TriggerName.fieldName}}" } } — use EXACT node labels and field names from outputs arrays. ' +
                                        'For trigger data: {{input.fieldName}} from current trigger. ' +
                                        'For expression nodes: { "expression": "io.pipe({ result: io.getPipedDataOf(\'NodeName\').fieldName })" }. ' +
                                        'NEVER use bare node labels like {{NodeName.field}} — always use {{node.NodeName.field}} or {{input.field}}.'
                                }
                            }
                        }
                    }
                },
                (args) => {
                    var nodeIdx = -1
                    if (args.node_id !== undefined) {
                        nodeIdx = this.nodes.findIndex(n => n.id == args.node_id)
                    }
                    if (nodeIdx === -1 && args.node_name) {
                        nodeIdx = this.nodes.findIndex(n => n.name === args.node_name)
                    }
                    if (nodeIdx === -1) {
                        return { error: 'Node not found', available: this.nodes.map(n => ({ id: n.id, name: n.name })) }
                    }
                    var nodes = JSON.parse(JSON.stringify(this.nodes))
                    var nodeData = nodes[nodeIdx].data
                    // Deep-merge inputs for integration nodes
                    if (args.data.inputs !== undefined && nodeData.inputs !== undefined) {
                        Object.assign(nodeData.inputs, args.data.inputs)
                        var rest = Object.assign({}, args.data)
                        delete rest.inputs
                        Object.assign(nodeData, rest)
                    } else {
                        Object.assign(nodeData, args.data)
                    }
                    this.nodes = nodes
                    this.content = JSON.parse(JSON.stringify(this.nodes))
                    this.version++
                    this.saveToHistory('Node data updated by AI')
                    this.$emit('save', this.content)
                    return { updated: true, node_id: nodes[nodeIdx].id, name: nodes[nodeIdx].name }
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'save_workflow',
                        description: 'Persists the current workflow canvas (all nodes and connections) to the backend. Call this once after all nodes have been added, configured, and wired up.',
                        parameters: { type: 'object', properties: {} }
                    }
                },
                () => {
                    return this.$workflowApi.get('/workflow/' + this.uuid).then(resp => {
                        var workflow = resp.data
                        var targetVersion = workflow.dev_version
                        var settings = null
                        if (targetVersion) {
                            settings = (workflow.settings || []).find(s => s.version === targetVersion)
                        }
                        if (!settings && workflow.settings && workflow.settings.length > 0) {
                            settings = workflow.settings[workflow.settings.length - 1]
                        }
                        if (settings) {
                            settings.nodes = JSON.parse(JSON.stringify(this.nodes))
                        }
                        return this.$workflowApi.patch('/workflow/' + this.uuid, workflow).then(() => ({
                            saved: true,
                            version: settings ? settings.version : null,
                            nodes_saved: this.nodes.length
                        }))
                    }).catch(err => ({ error: err.message || 'Save failed' }))
                }
            )

            window.kortex.registerTool(
                {
                    type: 'function',
                    function: {
                        name: 'clear_workflow_canvas',
                        description: 'Removes all nodes and connections from the workflow canvas. Use before building a workflow from scratch to start with a clean slate.',
                        parameters: { type: 'object', properties: {} }
                    }
                },
                () => {
                    var count = this.nodes.length
                    this.nodes = []
                    this.connections = []
                    this.content = []
                    this.version++
                    this.saveToHistory('Canvas cleared by AI')
                    this.$emit('save', this.content)
                    return { cleared: true, removed_nodes: count }
                }
            )
        }
    }
}
</script>
